// Centralized status, severity, priority, and category color/label maps.
// Import from here instead of defining inline in each component.
// Checked by pr-code-review.yml — inline maps in components will be flagged.

import type { ElementType } from "react";
import type { PriorityFlagCategory } from "./npdPriority";
import {
  Building2,
  User,
  Globe,
  CheckCircle,
  // Label scan pass icons (plan P-5a):
  Circle,
  Loader2,
  Package,
  ArrowRightLeft,
  // Sample dispatch status icons (Task 1.20):
  Clock,
  FileCheck2,
  Truck,
  PackageCheck,
  AlertTriangle as AlertTriangleIcon,
  XCircle as XCircleIcon,
  // Customer email status icons:
  MinusCircle,
  Hourglass,
  CheckCircle2,
  XOctagon,
  // Document status icons:
  FileText,
  FileClock,
  AlertCircle,
  // Signing (OpenSign) status icons:
  Send,
  Eye,
  PenLine,
  CalendarX,
  Ban,
  // MO material availability tier icons (Wave D4):
  PhoneCall,
  Ship,
  Anchor,
  HelpCircle,
  // RKB verdict chip icons (Increment 4 Task 1):
  UserCheck,
} from "lucide-react";

// ── NCR ──────────────────────────────────────────────────────────────────────

export const NCR_STATUS_COLORS: Record<string, string> = {
  open: "bg-destructive/20 text-destructive",
  investigation: "bg-warning/20 text-warning-foreground",
  capa_pending: "bg-primary/20 text-primary",
  capa_in_progress: "bg-primary/20 text-primary",
  verification: "bg-success/20 text-success",
  closed: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground",
} as const;

export const NCR_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  investigation: "Investigation",
  capa_pending: "CAPA Pending",
  capa_in_progress: "CAPA In Progress",
  verification: "Verification",
  closed: "Closed",
  cancelled: "Cancelled",
} as const;

export const NCR_SEVERITY_COLORS: Record<string, string> = {
  minor: "bg-muted text-muted-foreground",
  major: "bg-warning/20 text-warning-foreground",
  critical: "bg-destructive/20 text-destructive",
  observation: "bg-muted text-muted-foreground",
} as const;

// ── CAPA ─────────────────────────────────────────────────────────────────────

export const CAPA_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  open: "bg-primary/20 text-primary",
  in_progress: "bg-warning/20 text-warning-foreground",
  pending_review: "bg-secondary/20 text-secondary-foreground",
  closed: "bg-success/20 text-success",
  cancelled: "bg-muted text-muted-foreground",
} as const;

export const CAPA_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  open: "Open",
  in_progress: "In Progress",
  pending_review: "Pending Review",
  closed: "Closed",
  cancelled: "Cancelled",
} as const;

export const CAPA_PRIORITY_COLORS: Record<string, string> = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-primary/20 text-primary",
  high: "bg-warning/20 text-warning-foreground",
  critical: "bg-destructive/20 text-destructive",
} as const;

export const CAPA_CATEGORY_LABELS: Record<string, string> = {
  quality: "Quality",
  food_defense: "Food Defense",
  safety: "Safety",
  regulatory: "Regulatory",
} as const;

// ── Audit ────────────────────────────────────────────────────────────────────

export const AUDIT_STATUS_COLORS: Record<string, string> = {
  planned: "bg-muted text-muted-foreground",
  in_progress: "bg-primary/20 text-primary",
  completed: "bg-success/20 text-success",
  cancelled: "bg-muted text-muted-foreground",
} as const;

export const AUDIT_STATUS_LABELS: Record<string, string> = {
  planned: "Planned",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
} as const;

export const AUDIT_TYPE_LABELS: Record<string, string> = {
  internal: "Internal",
  external: "External",
  supplier: "Supplier",
  customer: "Customer",
  regulatory: "Regulatory",
} as const;

export const AUDIT_FINDING_SEVERITY_COLORS: Record<string, string> = {
  minor: "bg-muted text-muted-foreground",
  major: "bg-warning/20 text-warning-foreground",
  critical: "bg-destructive/20 text-destructive",
  observation: "bg-muted text-muted-foreground",
} as const;

// ── Complaint ────────────────────────────────────────────────────────────────

export const COMPLAINT_STATUS_COLORS: Record<string, string> = {
  received: "bg-muted text-muted-foreground",
  investigating: "bg-warning/20 text-warning-foreground",
  resolution_pending: "bg-primary/20 text-primary",
  resolved: "bg-success/20 text-success",
  closed: "bg-muted text-muted-foreground",
  escalated: "bg-destructive/20 text-destructive",
} as const;

export const COMPLAINT_STATUS_LABELS: Record<string, string> = {
  received: "Received",
  investigating: "Investigating",
  resolution_pending: "Resolution Pending",
  resolved: "Resolved",
  closed: "Closed",
  escalated: "Escalated",
} as const;

export const COMPLAINT_PRIORITY_COLORS: Record<string, string> = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-primary/20 text-primary",
  high: "bg-warning/20 text-warning-foreground",
  urgent: "bg-destructive/20 text-destructive",
} as const;

// ── Quality Check ────────────────────────────────────────────────────────────

export const QC_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  submitted: "bg-primary/20 text-primary",
  reviewed: "bg-warning/20 text-warning-foreground",
  approved: "bg-success/20 text-success",
  rejected: "bg-destructive/20 text-destructive",
} as const;

// ── Shipping ─────────────────────────────────────────────────────────────────

export const SHIPPING_STATUS_COLORS: Record<string, string> = {
  new: "bg-info/10 text-info",
  draft: "bg-muted text-muted-foreground",
  quoted: "bg-warning/10 text-warning",
  booked: "bg-primary/10 text-primary",
  dispatched: "bg-chart-3/10 text-chart-3",
  in_transit: "bg-chart-3/10 text-chart-3",
  delivered: "bg-success/10 text-success",
  cancelled: "bg-destructive/10 text-destructive",
} as const;

// TrackShip unified tracking statuses
export const TRACKING_STATUS_COLORS: Record<string, string> = {
  unknown: "bg-gray-100 text-gray-700",
  pre_transit: "bg-gray-100 text-gray-700",
  in_transit: "bg-blue-100 text-blue-700",
  out_for_delivery: "bg-amber-100 text-amber-700",
  delivered: "bg-emerald-100 text-emerald-700",
  available_for_pickup: "bg-purple-100 text-purple-700",
  delivery_failure: "bg-red-100 text-red-700",
  return_to_sender: "bg-red-100 text-red-700",
  exception: "bg-red-100 text-red-700",
  on_hold: "bg-amber-100 text-amber-700",
  label_cancelled: "bg-gray-100 text-gray-500",
} as const;

export const TRACKING_STATUS_LABELS: Record<string, string> = {
  unknown: "Unknown",
  pre_transit: "Pre-Transit",
  in_transit: "In Transit",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  available_for_pickup: "Available for Pickup",
  delivery_failure: "Delivery Failed",
  return_to_sender: "Returned",
  exception: "Exception",
  on_hold: "On Hold",
  label_cancelled: "Cancelled",
} as const;

// ── Smart Shipment Planning Module ──

export const SHIPMENT_PLAN_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  locked: "Locked",
  overridden: "Overridden",
  completed: "Completed",
} as const;

export const SHIPMENT_PLAN_STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  locked: "bg-blue-100 text-blue-700",
  overridden: "bg-purple-100 text-purple-700",
  completed: "bg-emerald-100 text-emerald-700",
} as const;

export const SHIPMENT_PROFILE_STATUS_LABELS: Record<string, string> = {
  active: "Active",
  pending_review: "Pending Review",
  archived: "Archived",
} as const;

export const SHIPMENT_PROFILE_STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  pending_review: "bg-amber-100 text-amber-700",
  archived: "bg-gray-100 text-gray-500",
} as const;

export const SPLIT_MODE_LABELS: Record<string, string> = {
  NONE: "No Split",
  PERCENTAGE: "Percentage",
  FIXED_UNITS: "Fixed Units",
  BY_SKU: "By SKU",
  BY_FLAVOUR: "By Flavour",
} as const;

export const REMAINDER_ROUTING_LABELS: Record<string, string> = {
  PRIMARY: "Primary Address",
  LAST_ADDRESS: "Last Address",
  DISTRIBUTE_PROPORTIONALLY: "Distribute Proportionally",
  ALERT_AND_HOLD: "Alert & Hold",
} as const;

export const STACKING_PATTERN_LABELS: Record<string, string> = {
  COLUMN: "Column",
  BRICK: "Brick",
  PINWHEEL: "Pinwheel",
  CUSTOM: "Custom",
} as const;

export const ESCALATION_SEVERITY_COLORS: Record<string, string> = {
  info: "bg-blue-100 text-blue-700",
  warning: "bg-amber-100 text-amber-700",
  critical: "bg-red-100 text-red-700",
} as const;

/** Card-level classes for escalation severity — border + background + text */
export const ESCALATION_SEVERITY_CARD_CLASSES: Record<string, string> = {
  critical: "bg-red-50 border-red-200 text-red-800",
  warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
  info: "bg-blue-50 border-blue-200 text-blue-800",
} as const;

// Map Transdirect courier keys → TrackShip carrier slugs
export const TRANSDIRECT_TO_TRACKSHIP: Record<string, string | null> = {
  allied: "allied-express-au",
  tnt: "tnt-au",
  toll: "toll-ipec",
  "toll-priority": "toll-ipec",
  fastway: "aramex-au",
  startrack: "startrack",
  "startrack-premium": "startrack",
  "hunter-express": "hunter-express",
  northline: "northline",
  couriersplease: "couriers-please",
  mainfreight: "mainfreight",
  "border-express": "border-express",
  "australia-post": "australia-post",
  dhl: "dhl",
  "dhl-express": "dhl",
  // Carriers without confirmed TrackShip slug — null = fallback to basic tracking
  "xpress-freight": null,
} as const;

// ── Tasks ────────────────────────────────────────────────────────────────────

export const TASK_STATUS_COLORS: Record<string, string> = {
  open: "bg-destructive/15 text-destructive",
  in_progress: "bg-warning/15 text-warning",
  review: "bg-muted text-muted-foreground",
  done: "bg-success/15 text-success",
} as const;

export const TASK_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  review: "In Review",
  done: "Done",
} as const;

export const TASK_PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-destructive text-destructive-foreground",
  high: "bg-warning text-warning-foreground",
  normal: "bg-primary text-primary-foreground",
  low: "bg-muted text-muted-foreground",
} as const;

// ── BOM ────────────────────────────────────────────────────────────────────

export const BOM_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  approved: "bg-success/15 text-success",
  superseded: "bg-warning/15 text-warning",
  archived: "bg-slate-100 text-slate-500",
} as const;

export const BOM_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  approved: "Approved",
  superseded: "Superseded",
  archived: "Archived",
} as const;

// ── Manufacturing Stage ────────────────────────────────────────────────────

export const STAGE_COLORS: Record<string, string> = {
  for: "bg-blue-100 text-blue-700 border-blue-200",
  fil: "bg-amber-100 text-amber-700 border-amber-200",
  fcp: "bg-emerald-100 text-emerald-700 border-emerald-200",
} as const;

export const STAGE_LABELS: Record<string, string> = {
  for: "FOR — Formulation",
  fil: "FIL — Filling",
  fcp: "FCP — Fill/Cap/Pack",
} as const;

export const STAGE_SHORT_LABELS: Record<string, string> = {
  for: "FOR",
  fil: "FIL",
  fcp: "FCP",
} as const;

// ── Item Type (centralised — replaces hardcoded maps in stock item views) ──

export const ITEM_TYPE_LABELS: Record<string, string> = {
  raw_material: "Raw Material",
  packaging: "Packaging",
  finished_good: "Finished Good",
  wip: "Work in Progress",
  // Consumables module (non-production maintenance/PPE/cleaning/workshop stock).
  // Carries the CNS item_class; excluded from BOM stages.
  consumable: "Consumable",
} as const;

export const ITEM_TYPE_COLORS: Record<string, string> = {
  raw_material: "bg-blue-100 text-blue-700 border-blue-200",
  packaging: "bg-purple-100 text-purple-700 border-purple-200",
  finished_good: "bg-emerald-100 text-emerald-700 border-emerald-200",
  wip: "bg-amber-100 text-amber-700 border-amber-200",
  consumable: "bg-slate-100 text-slate-700 border-slate-200",
} as const;

// ── Item Class (material classification for BOM stage validation) ──────────

export const ITEM_CLASS_LABELS: Record<string, string> = {
  CHE: "Chemical",
  CAP: "Capsule Shell",
  FLM: "Flexible Film",
  BTL: "Bottle/Tub",
  LID: "Lid/Cap",
  SCP: "Scoop",
  MIS: "Miscellaneous",
  LBL: "Label",
  // CTU = Consumer Trade Unit (pouch primary packaging, replaces
  // BTL+LBL on pouch FCP BOMs — see MRPEASY_GROUP_NAMES.CTU and
  // useMRPeasyBomExport's isPouch branch).
  CTU: "Pouch (CTU)",
  CTN: "Carton",
  PLT: "Pallet",
  IVS: "Intermediary Vessel",
  // CNS = Consumable (non-production maintenance/PPE/cleaning/workshop stock).
  // Not BOM-eligible (absent from STAGE_ALLOWED_CLASSES and
  // chk_bom_components_item_class). Consumables Ordering module.
  CNS: "Consumable",
} as const;

/**
 * Item classes that can carry a scoop spec (npd_scoops link). SCP is the
 * dedicated class introduced in 20260728100000_stock_items_scoop_link_and_documents.sql,
 * but legacy scoop inventory is still classified as MIS — treating both as
 * scoop-eligible lets operators populate npd_scoops specs against existing
 * stock today. A future MIS→SCP reclass migration can tighten this to
 * `["SCP"]` without touching every gate site.
 */
export const SCOOP_ELIGIBLE_CLASSES = ["SCP", "MIS"] as const;

export function isScoopEligibleClass(itemClass: string | null | undefined): boolean {
  if (!itemClass) return false;
  return (SCOOP_ELIGIBLE_CLASSES as readonly string[]).includes(itemClass);
}

/**
 * MRPeasy "Group name" labels per item_class — used in the BOM CSV
 * exporter (src/lib/mrpeasyBomCsvExport.ts). The MRPeasy import format
 * carries both a Group number (= item_class) and a Group name (the
 * descriptive label) on every line.
 *
 * These values mirror the operator's MRPeasy "Stock settings →
 * Product groups" register exactly (verified 2026-05-01). Drift
 * surfaces as "group not found" on import — when MRPeasy adds or
 * renames a group, update this constant in one place.
 *
 * Special case: SCP (scoops) is rolled into MIS for MRPeasy purposes
 * — see toMrpeasyGroup() below. MRPeasy doesn't have a dedicated
 * scoops group; scoops live under MIS.
 *
 * FOR / FIL / FCP appear here because sub-BOM reference lines carry
 * the parent stage's class (e.g. an FCP line that references the FIL
 * output gets itemClass="FIL"), and the CSV exporter resolves the
 * group label from that class. Without these entries the sub-BOM
 * lines would fall through to the "MISCELLANEOUS" default.
 */
export const MRPEASY_GROUP_NAMES: Record<string, string> = {
  FOR: "Blends",
  BTL: "BOTTLES / JARS",
  // CTU = Consumer Trade Unit — the pre-printed pouch SKU that replaces
  // the BTL+LBL pair on a pouch product's FCP BOM. Operator-spec
  // PR #451: pouches don't need a separate label.
  // Tenant title for group CTU is "UNIT CARTON" (GET /product-groups, 2026-09-18);
  // owner ruling: pouches stay under it. Keep in step with mrpeasyExportTemplates.ts
  // and mrpeasyBundleCsv.classToGroup — three copies of one label.
  CTU: "UNIT CARTON",
  CAP: "CAPSULES",
  ZZZ: "CONSUMABLES",
  CSB: "CUSTOMER SUPPLIED BLEND",
  FCP: "FILL/CAP/PACK",
  FIL: "Filling (encapsulation)",
  FRE: "FREIGHT",
  SMP: "Lab Samples",
  LBL: "LABEL",
  LID: "LIDS / CLOSURES",
  MIS: "MISCELLANEOUS (PRODUCT RELATED)",
  CHE: "PRIMARY INGREDIENTS",
  QTS: "QUOTES",
  FLM: "Rewind Film",
  SCT: "Sachets",
  CTN: "SHIPPER",
  SLV: "SHRINK SLEEVE",
  SRV: "SERVICES",
};

/**
 * Resolves the (Group number, Group name) pair MRPeasy expects on
 * each BOM CSV line for an item with the given QF item_class.
 *
 * SCP items are normalised to MIS — operator confirmed scoops live
 * in the MIS group on the MRPeasy side. If a future MRPeasy setup
 * adds a dedicated scoops group, drop the SCP branch and the rest
 * of the export pipeline picks it up automatically.
 */
export function toMrpeasyGroup(qfItemClass: string | null | undefined): {
  groupNumber: string;
  groupName: string;
} {
  const cls = (qfItemClass ?? "").trim().toUpperCase();
  // Per operator: scoops → MIS group on MRPeasy side
  const normalized = cls === "SCP" ? "MIS" : cls;
  return {
    groupNumber: normalized,
    groupName: MRPEASY_GROUP_NAMES[normalized] ?? "MISCELLANEOUS",
  };
}

/**
 * Format a QF item code for MRPeasy import. MRPeasy DISPLAYS codes
 * with a space + decimal padding (e.g. "CHE 461.00"), but the import
 * CSV expects the canonical 4-digit-padded form (e.g. "CHE0461").
 * Operator-spec format (PR #442 follow-up): when MRPeasy-display
 * markers are detected (whitespace OR a decimal point), we strip
 * the noise AND zero-pad the numeric suffix to a minimum of 4
 * digits to match MRPeasy's modern canonical form.
 *
 * Examples:
 *   "CHE 461.00" → "CHE0461"  (display format → canonical)
 *   "CHE 587.00" → "CHE0587"  (display format → canonical)
 *   "CHE0461"    → "CHE0461"  (already canonical, no-op)
 *   "CHE320"     → "CHE320"   (legacy unpadded MRPeasy code preserved
 *                              — padding would create a different code
 *                              that may not exist in MRPeasy)
 *   "CHE12345"   → "CHE12345" (longer suffixes not truncated)
 *
 * The "preserve already-clean" rule prevents accidental drift on the
 * legacy unpadded codes (CHE320, CHE272 etc.) that genuinely exist in
 * MRPeasy alongside the modern padded form.
 *
 * Bracketed placeholders (e.g. "[FOR BOM]", "[FIL BOM]") are passed
 * through verbatim — the BOM exporters use them as sub-assembly
 * references when production codes haven't been allocated yet, and
 * stripping the inner space would silently turn "[FOR BOM]" into
 * "[FORBOM]" in the rendered output. The bracket guard keeps these
 * placeholders human-readable in both the PDF and CSV surfaces.
 */
export function formatMrpeasyPartNo(code: string | null | undefined): string {
  if (!code) return "";
  const raw = String(code);
  if (/^\[.*\]$/.test(raw)) return raw;

  // Trim incidental surrounding whitespace BEFORE checking for display
  // markers. Without the trim, a clean legacy code like "CHE320 "
  // (trailing space from a hand entry or a CSV import) would be
  // mis-detected as MRPeasy-display format and zero-padded to "CHE0320"
  // — a code that may not exist in MRPeasy.
  const s = raw.trim();

  // After trimming, display markers are: an INTERNAL whitespace that
  // separates the alpha prefix from the numeric suffix (the canonical
  // "CHE 587" shape), OR a decimal point anywhere (only present in
  // MRPeasy's "CHE 587.00" display padding). Either signal means we
  // should normalise to canonical 4-digit form.
  const hadDisplayMarkers = /[A-Z]\s+\d|\./.test(s);
  const stripped = s
    .replace(/\s+/g, "")        // strip spaces (MRPeasy display format)
    .replace(/\.0+$/u, "");     // strip trailing .00 / .0 (display padding)

  if (!hadDisplayMarkers) return stripped;

  const m = stripped.match(/^([A-Z]+)(\d+)$/);
  if (!m) return stripped;
  const [, prefix, num] = m;
  return `${prefix}${num.padStart(4, "0")}`;
}

export const ITEM_CLASS_COLORS: Record<string, string> = {
  CHE: "bg-blue-100 text-blue-700 border-blue-200",
  CAP: "bg-amber-100 text-amber-700 border-amber-200",
  FLM: "bg-amber-100 text-amber-700 border-amber-200",
  BTL: "bg-emerald-100 text-emerald-700 border-emerald-200",
  LID: "bg-emerald-100 text-emerald-700 border-emerald-200",
  SCP: "bg-teal-100 text-teal-700 border-teal-200",
  MIS: "bg-gray-100 text-gray-700 border-gray-200",
  LBL: "bg-purple-100 text-purple-700 border-purple-200",
  // CTU shares LBL's purple — both are consumer-facing branded packaging
  // (LBL = jar label, CTU = printed pouch). Distinguish by class code text.
  CTU: "bg-purple-100 text-purple-700 border-purple-200",
  CTN: "bg-orange-100 text-orange-700 border-orange-200",
  PLT: "bg-stone-100 text-stone-700 border-stone-200",
  IVS: "bg-cyan-100 text-cyan-700 border-cyan-200",
  // Consumables: slate/neutral, distinct from MIS (gray) and PLT (stone).
  CNS: "bg-slate-100 text-slate-700 border-slate-200",
} as const;

/**
 * Is this stock item a chemical (CHE), for the purpose of showing CHE-only controls?
 *
 * READS THE ITEM CODE, NOT JUST THE CLASS COLUMN, and that is the whole point.
 * `stock_items.item_class` is NULL on 2,724 of 2,725 live rows — the single
 * populated value is 'CNS', and NOTHING is 'CHE'. So `item_class === "CHE"`,
 * which is how the Chemical Sub-class picker was gated, is false for every stock
 * item in the system: the control has never been visible, which is exactly why
 * `che_sub_class` is unset on all 2,708 and why the natural/artificial flavour
 * classification had nowhere to be entered.
 *
 * `item_code` IS populated — 776 items carry the CHE prefix — and the prefix is
 * a documented architecture constant (CLAUDE.md §16: CHE / CAP / FLM / BTL /
 * LID / LBL / CTN / PLT / IVS), not a naming convention that might drift.
 *
 * The class column still wins where it is set, so populating it later changes
 * nothing here. Display-only: this decides whether a control renders, never what
 * a label declares.
 */
export function isCheItem(
  itemClass: string | null | undefined,
  itemCode: string | null | undefined,
): boolean {
  if (String(itemClass ?? "").toUpperCase() === "CHE") return true;
  return String(itemCode ?? "").trim().toUpperCase().startsWith("CHE");
}

export const CHE_SUB_CLASS_LABELS: Record<string, string> = {
  active: "Active",
  flavour: "Flavour",
  food_acid: "Food Acid",
  sweetener: "Sweetener",
  excipient: "Excipient",
  processing_aid: "Processing Aid",
  filler: "Filler",
} as const;

// Which item_class values are valid for each item_type
export const ITEM_TYPE_TO_CLASSES: Record<string, string[]> = {
  raw_material: ["CHE", "IVS"],
  packaging: ["CAP", "FLM", "BTL", "LID", "SCP", "MIS", "LBL", "CTU", "CTN", "PLT", "IVS"],
  finished_good: [],
  wip: [],
  // Consumables module: the consumable item_type carries exactly the CNS class.
  consumable: ["CNS"],
} as const;

// Which item_class values are valid at each manufacturing stage
export const STAGE_ALLOWED_CLASSES: Record<string, string[]> = {
  for: ["CHE"],
  fil: ["CAP", "FLM", "IVS"],
  // CTU is FCP-only (pouches only exist as a final consumer pack).
  fcp: ["BTL", "LID", "SCP", "MIS", "LBL", "CTU", "CTN", "PLT", "IVS"],
} as const;

// FCP stage structural validation — mandatory components that must be present.
//
// NOTE: this list applies to STANDARD (jar) FCP BOMs. Pouch products
// (npd_packaging_configs.packaging_mode = 'pouch') have a different mandatory
// set: CTU + CTN, with no BTL/LID/LBL. The current consumer
// (BomValidationPanel.tsx) does not yet receive packaging_mode — pouch
// validation is gated on the server-side verify_bom_qa C7 check (which IS
// pouch-aware as of 20260805120000) and the BOM Preview tab's render path.
// Follow-up: thread packaging_mode through BomValidationPanel and switch
// this constant to a function `getFcpMandatoryClasses(packagingMode)`.
export const FCP_MANDATORY_CLASSES: string[] = ["BTL", "LID", "LBL", "CTN"];
export const FCP_MANDATORY_CLASSES_POUCH: string[] = ["CTU", "CTN"];
// FCP stage optional classes — shown as present/absent without warning
export const FCP_OPTIONAL_CLASSES: string[] = ["MIS", "PLT", "IVS"];

// ── Routing ────────────────────────────────────────────────────────────────

export const ROUTING_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  approved: "bg-success/15 text-success",
  superseded: "bg-warning/15 text-warning",
} as const;

export const ROUTING_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  approved: "Approved",
  superseded: "Superseded",
} as const;

// ── Goods Receipt ───────────────────────────────────────────────────────────

export const GR_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  received: "bg-info/15 text-info",
  inspecting: "bg-warning/15 text-warning",
  accepted: "bg-success/15 text-success",
  rejected: "bg-destructive/15 text-destructive",
} as const;

export const GR_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  received: "Received",
  inspecting: "Inspecting",
  accepted: "Accepted",
  rejected: "Rejected",
} as const;

// ── Lot Quality Status ──────────────────────────────────────────────────────

export const LOT_QUALITY_COLORS: Record<string, string> = {
  on_order: "bg-muted text-muted-foreground",
  available: "bg-success/15 text-success",
  released: "bg-success/15 text-success",
  on_hold: "bg-warning/15 text-warning",
  inspection: "bg-info/15 text-info",
  rejected: "bg-destructive/15 text-destructive",
  expired: "bg-orange-100 text-orange-700",
  quarantine: "bg-orange-100 text-orange-700",
} as const;

export const LOT_QUALITY_LABELS: Record<string, string> = {
  on_order: "On Order",
  available: "Available",
  released: "Released",
  on_hold: "On Hold",
  inspection: "Inspection",
  rejected: "Rejected",
  expired: "Expired",
  quarantine: "Quarantine",
} as const;

// ── Stock Movement ──────────────────────────────────────────────────────────

export const MOVEMENT_TYPE_COLORS: Record<string, string> = {
  receive: "bg-success/15 text-success",
  issue: "bg-destructive/15 text-destructive",
  transfer: "bg-info/15 text-info",
  adjust: "bg-warning/15 text-warning",
  write_off: "bg-destructive/15 text-destructive",
  scrap: "bg-orange-100 text-orange-700",
  return: "bg-primary/15 text-primary",
  ship: "bg-chart-3/15 text-chart-3",
  ship_reversal: "bg-warning/15 text-warning",
  correction: "bg-purple-100 text-purple-700",
  correction_replacement: "bg-purple-100 text-purple-700",
} as const;

export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  receive: "Receive",
  issue: "Issue",
  transfer: "Transfer",
  adjust: "Adjustment",
  write_off: "Write Off",
  scrap: "Scrap",
  return: "Return",
  ship: "Ship",
  ship_reversal: "Ship Reversal",
  correction: "Correction (Reversal)",
  correction_replacement: "Correction (Replacement)",
} as const;

// ── Adjustment Reason Codes ──────────────────────────────────────────────────

export const ADJUSTMENT_REASON_LABELS: Record<string, string> = {
  recount_correction: "Recount Correction",
  damaged: "Damaged",
  spillage: "Spillage",
  expired: "Expired",
  found_stock: "Found Stock",
  sampling_qc: "Sampling / QC",
  write_off: "Write Off",
  other: "Other",
} as const;

export const ADJUSTMENT_REASON_COLORS: Record<string, string> = {
  recount_correction: "bg-info/15 text-info",
  damaged: "bg-destructive/15 text-destructive",
  spillage: "bg-warning/15 text-warning",
  expired: "bg-orange-100 text-orange-700",
  found_stock: "bg-success/15 text-success",
  sampling_qc: "bg-primary/15 text-primary",
  write_off: "bg-destructive/15 text-destructive",
  other: "bg-muted text-muted-foreground",
} as const;

// ── Movement Approval Status ─────────────────────────────────────────────────

export const MOVEMENT_APPROVAL_LABELS: Record<string, string> = {
  not_required: "N/A",
  pending: "Pending Approval",
  approved: "Approved",
  rejected: "Rejected",
} as const;

export const MOVEMENT_APPROVAL_COLORS: Record<string, string> = {
  not_required: "bg-muted text-muted-foreground",
  pending: "bg-warning/15 text-warning",
  approved: "bg-success/15 text-success",
  rejected: "bg-destructive/15 text-destructive",
} as const;

// ── Production Job ─────────────────────────────────────────────────────────

export const PJ_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  confirmed: "bg-primary/15 text-primary",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
  cancelled: "bg-muted text-muted-foreground",
} as const;

export const PJ_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
} as const;

export const PJ_PRIORITY_COLORS: Record<string, string> = {
  low: "bg-muted text-muted-foreground",
  normal: "bg-primary/15 text-primary",
  high: "bg-warning/15 text-warning",
  urgent: "bg-destructive/15 text-destructive",
} as const;

export const PJ_PRIORITY_LABELS: Record<string, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
} as const;

export const PJ_SOURCE_COLORS: Record<string, string> = {
  customer_order: "bg-cyan-100/80 text-cyan-700",
  manual_override: "bg-amber-100/80 text-amber-700",
} as const;

export const PJ_SOURCE_LABELS: Record<string, string> = {
  customer_order: "From Order",
  manual_override: "Manual Override",
} as const;

// ── Manufacturing Order ───────────────────────────────────────────────────

export const MO_STATUS_COLORS: Record<string, string> = {
  planned: "bg-muted text-muted-foreground",
  confirmed: "bg-primary/15 text-primary",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
  cancelled: "bg-muted text-muted-foreground",
} as const;

export const MO_STATUS_LABELS: Record<string, string> = {
  planned: "Planned",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
} as const;

export const MO_EXECUTION_COLORS: Record<string, string> = {
  not_started: "bg-muted text-muted-foreground",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
  on_hold: "bg-amber-100 text-amber-700",
} as const;

export const MO_EXECUTION_LABELS: Record<string, string> = {
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
  on_hold: "On Hold",
} as const;

// ── Material Readiness (allocation status) ────────────────────────────────

export const MATERIAL_READINESS_COLORS: Record<string, string> = {
  // `consumed` is a completed order's terminal, SATISFIED state — slate, matching
  // MO_ITEM_STATUS_COLORS.consumed. It must never carry `blocked`'s destructive red:
  // a finished job rendering as "Materials Short" is the defect this value closes.
  consumed: "bg-slate-100 text-slate-700",
  ready: "bg-green-100 text-green-700",
  incoming: "bg-blue-100 text-blue-700",
  blocked: "bg-red-100 text-red-700",
  unknown: "bg-muted text-muted-foreground",
} as const;

export const MATERIAL_READINESS_LABELS: Record<string, string> = {
  consumed: "Materials Consumed",
  ready: "Materials Ready",
  incoming: "Incoming",
  blocked: "Materials Short",
  unknown: "Not Checked",
} as const;

// ── MO BOM line status (manufacturing_order_items.status) ─────────────────
// The full vocabulary the mrpeasy-sync writer emits. `consumed` and `booked` were both
// written by the sync but had NO badge case in ManufacturingOrdersTab, so they rendered as
// "Unknown" — `booked` on 33 live confirmed MOs, and `consumed` on every completed MO once
// buildConsumedMoItemRows started deriving lines from the consumption record
// (docs/mo-bom-fulfilment-investigation-20260820.md).
//
// `consumed` is a TERMINAL, SATISFIED state, not a warning: the material was issued to the
// order and the BOM line is closed. It must never share the destructive/amber styling that
// `shortage`/`partial` carry — a finished order rendering red is the defect this vocabulary
// exists to close.
export const MO_ITEM_STATUS_COLORS: Record<string, string> = {
  consumed: "bg-slate-100 text-slate-700",
  booked: "bg-blue-100 text-blue-700",
  available: "bg-emerald-100 text-emerald-700",
  partial: "bg-amber-100 text-amber-700",
  shortage: "bg-red-100 text-red-700",
  unknown: "bg-muted text-muted-foreground",
} as const;

export const MO_ITEM_STATUS_LABELS: Record<string, string> = {
  consumed: "Consumed",
  booked: "Booked",
  available: "Available",
  partial: "Partial",
  shortage: "Shortage",
  unknown: "Unknown",
} as const;

/**
 * Resolve a raw `manufacturing_order_items.status` to a key both MO_ITEM_STATUS maps hold.
 *
 * WHY THIS IS A FUNCTION AND NOT AN INLINE TERNARY. The obvious spelling,
 * `status in MO_ITEM_STATUS_LABELS`, is WRONG: `in` walks the prototype chain, so
 * "toString", "constructor", "valueOf" and "__proto__" all pass the guard and then resolve
 * to a FUNCTION (or Object.prototype) — which React renders as the badge label and which is
 * interpolated raw into className. The same shape — a prototype-chain lookup read as a hit —
 * is recorded in NOTES-NPD for the caffeine proposer (remediation P1-1), where it returned
 * NaN as a success.
 *
 * Keeping it beside the vocabulary rather than in the component means every future consumer
 * inherits the safe lookup instead of re-deriving it (CLAUDE.md §5), and it puts the logic in
 * src/lib/, which tsconfig.strict.json gates.
 */
export function moItemStatusKey(status: string | null | undefined): string {
  return status && Object.prototype.hasOwnProperty.call(MO_ITEM_STATUS_LABELS, status)
    ? status
    : "unknown";
}

// ── MO Material Availability (Wave D4 — pegged readiness signal & tier rollup) ──
// Drives the per-component readiness table + the strict-worst MO rollup badge.
// Keyed by BOTH the component/rollup STATUS set {ready_now, forecast, short, unscheduled}
// AND the underlying tier names the get_mo_material_readiness RPC surfaces
// (on_hand_free, consignment_drawable, consignment_inbound_wh, blanket_remaining, uncovered).
// Spec §6 colour order (strict rollup, worst wins): RED > ORANGE > AMBER > BLUE > GREEN.
//
// Accessibility (spec §6 / NOTES-MES): NEVER colour alone — every consumer renders
// icon + short label + colour. Use MATERIAL_AVAILABILITY_ICONS for the icon so amber/orange
// and blue/green are disambiguated by glyph, not hue.
//
// iOS Wave-F mirror (CLAUDE.md §14): when StatusMaps.swift is built, mirror these new keys:
//   MATERIAL_AVAILABILITY_COLORS / _LABELS / _ICONS over the union
//   {ready_now, forecast, short, unscheduled, on_hand_free, consignment_drawable,
//    consignment_inbound_wh, blanket_remaining, uncovered}.
export const MATERIAL_AVAILABILITY_COLORS: Record<string, string> = {
  // Signal statuses (component + rollup)
  ready_now: "bg-green-100 text-green-700 border-green-200",
  forecast: "bg-blue-100 text-blue-700 border-blue-200",
  short: "bg-red-100 text-red-700 border-red-200",
  unscheduled: "bg-muted text-muted-foreground border-border",
  // Tier names (drilldown / limiting-tier badge)
  on_hand_free: "bg-green-100 text-green-700 border-green-200",
  consignment_drawable: "bg-blue-100 text-blue-700 border-blue-200",
  consignment_inbound_wh: "bg-amber-100 text-amber-700 border-amber-200",
  blanket_remaining: "bg-orange-100 text-orange-700 border-orange-200",
  uncovered: "bg-red-100 text-red-700 border-red-200",
} as const;

export const MATERIAL_AVAILABILITY_LABELS: Record<string, string> = {
  ready_now: "Ready now",
  forecast: "Forecast OK",
  short: "Short",
  unscheduled: "Unscheduled",
  on_hand_free: "On hand",
  consignment_drawable: "Drawdown",
  consignment_inbound_wh: "Inbound to WH",
  blanket_remaining: "Trigger shipment",
  uncovered: "Shortage",
} as const;

export const MATERIAL_AVAILABILITY_ICONS: Record<string, ElementType> = {
  ready_now: CheckCircle2,
  forecast: Truck,
  short: AlertTriangleIcon,
  unscheduled: HelpCircle,
  on_hand_free: PackageCheck,
  consignment_drawable: PhoneCall,    // call off from the consignment WH
  consignment_inbound_wh: Ship,       // on the water to the consignment WH
  blanket_remaining: Anchor,          // committed, trigger the WH shipment
  uncovered: AlertTriangleIcon,
} as const;

// ── MO Operation ──────────────────────────────────────────────────────────

export const MO_OP_STATUS_COLORS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
  skipped: "bg-muted text-muted-foreground",
} as const;

export const MO_OP_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  skipped: "Skipped",
} as const;

// ── MES Operation Schedule Status ────────────────────────────────────────────

export const MES_OP_SCHEDULE_STATUS_COLORS: Record<string, string> = {
  scheduled: "bg-primary/15 text-primary",
  in_progress: "bg-warning/15 text-warning",
  complete: "bg-success/15 text-success",
  cancelled: "bg-destructive/15 text-destructive",
  disrupted: "bg-destructive/15 text-destructive",
  rescheduled: "bg-info/15 text-info",
} as const;

export const MES_OP_SCHEDULE_STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  in_progress: "In Progress",
  complete: "Complete",
  cancelled: "Cancelled",
  disrupted: "Disrupted",
  rescheduled: "Rescheduled",
} as const;

// ── MES Scheduling Queue Status ─────────────────────────────────────────────

export const SCHEDULING_QUEUE_STATUS_COLORS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  processing: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
  failed: "bg-destructive/15 text-destructive",
  stale: "bg-muted text-muted-foreground",
} as const;

export const SCHEDULING_QUEUE_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
  stale: "Stale",
} as const;

// ── MES Scheduling Status (on manufacturing_orders) ─────────────────────────

export const MO_SCHEDULING_STATUS_COLORS: Record<string, string> = {
  pending_schedule: "bg-muted text-muted-foreground",
  scheduled: "bg-success/15 text-success",
  scheduling_queued: "bg-warning/15 text-warning",
  scheduling_failed: "bg-destructive/15 text-destructive",
} as const;

export const MO_SCHEDULING_STATUS_LABELS: Record<string, string> = {
  pending_schedule: "Pending Schedule",
  scheduled: "Scheduled",
  scheduling_queued: "Queue Processing",
  scheduling_failed: "Scheduling Failed",
} as const;

// ── CCP Batch Disposition ───────────────────────────────────────────────────

export const CCP_BATCH_DISPOSITION_COLORS: Record<string, string> = {
  hold: "bg-warning/15 text-warning",
  release: "bg-success/15 text-success",
  rework: "bg-info/15 text-info",
  destroy: "bg-destructive/15 text-destructive",
  pending: "bg-muted text-muted-foreground",
} as const;

export const CCP_BATCH_DISPOSITION_LABELS: Record<string, string> = {
  hold: "Hold",
  release: "Release",
  rework: "Rework",
  destroy: "Destroy",
  pending: "Pending",
} as const;

// ── CCP Monitoring Gap Status ───────────────────────────────────────────────

export const CCP_GAP_STATUS_COLORS: Record<string, string> = {
  open: "bg-warning/15 text-warning",
  resolved: "bg-success/15 text-success",
  waived_by_qa: "bg-info/15 text-info",
} as const;

export const CCP_GAP_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  resolved: "Resolved",
  waived_by_qa: "Waived by QA",
} as const;

// ── Consumption Variance Status ─────────────────────────────────────────────

export const CONSUMPTION_VARIANCE_STATUS_COLORS: Record<string, string> = {
  open: "bg-warning/15 text-warning",
  under_investigation: "bg-info/15 text-info",
  resolved: "bg-success/15 text-success",
  escalated: "bg-destructive/15 text-destructive",
} as const;

export const CONSUMPTION_VARIANCE_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  under_investigation: "Under Investigation",
  resolved: "Resolved",
  escalated: "Escalated",
} as const;

// ── Disruption Type ─────────────────────────────────────────────────────────

export const DISRUPTION_TYPE_COLORS: Record<string, string> = {
  equipment_down: "bg-destructive/15 text-destructive",
  material_hold: "bg-warning/15 text-warning",
  room_allergen: "bg-warning/15 text-warning",
  shift_change: "bg-muted text-muted-foreground",
  quality_hold: "bg-destructive/15 text-destructive",
  operator_unavailable: "bg-info/15 text-info",
} as const;

export const DISRUPTION_TYPE_LABELS: Record<string, string> = {
  equipment_down: "Equipment Down",
  material_hold: "Material Hold",
  room_allergen: "Room Allergen",
  shift_change: "Shift Change",
  quality_hold: "Quality Hold",
  operator_unavailable: "Operator Unavailable",
} as const;

// ── Disruption Traversal Status ─────────────────────────────────────────────

export const DISRUPTION_TRAVERSAL_COLORS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  phase1_complete: "bg-info/15 text-info",
  phase2_in_progress: "bg-warning/15 text-warning",
  complete: "bg-success/15 text-success",
  failed: "bg-destructive/15 text-destructive",
} as const;

export const DISRUPTION_TRAVERSAL_LABELS: Record<string, string> = {
  pending: "Pending",
  phase1_complete: "Phase 1 Complete",
  phase2_in_progress: "Phase 2 In Progress",
  complete: "Complete",
  failed: "Failed",
} as const;

// ── Scrap Disposition ───────────────────────────────────────────────────────

export const SCRAP_DISPOSITION_TYPE_COLORS: Record<string, string> = {
  waste_stream: "bg-muted text-muted-foreground",
  supplier_return: "bg-primary/15 text-primary",
  rework_hold: "bg-warning/15 text-warning",
  investigation_hold: "bg-info/15 text-info",
  within_allowance: "bg-success/15 text-success",
} as const;

export const SCRAP_DISPOSITION_TYPE_LABELS: Record<string, string> = {
  waste_stream: "Waste Stream",
  supplier_return: "Supplier Return",
  rework_hold: "Rework Hold",
  investigation_hold: "Investigation Hold",
  within_allowance: "Within Allowance",
} as const;

export const SCRAP_DISPOSITION_STATUS_COLORS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  disposed: "bg-success/15 text-success",
  waived: "bg-info/15 text-info",
} as const;

export const SCRAP_DISPOSITION_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  disposed: "Disposed",
  waived: "Waived",
} as const;

// ── Implicit Downtime Advisory Status ───────────────────────────────────────

export const IMPLICIT_DOWNTIME_STATUS_COLORS: Record<string, string> = {
  open: "bg-warning/15 text-warning",
  confirmed: "bg-success/15 text-success",
  dismissed: "bg-muted text-muted-foreground",
} as const;

export const IMPLICIT_DOWNTIME_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  confirmed: "Confirmed",
  dismissed: "Dismissed",
} as const;

// ── PSA (Plan Schedule Adherence) ───────────────────────────────────────────

export const PSA_RESULT_COLORS: Record<string, string> = {
  pass: "bg-success/15 text-success",
  near_miss: "bg-warning/15 text-warning",
  fail: "bg-destructive/15 text-destructive",
} as const;

export const PSA_RESULT_LABELS: Record<string, string> = {
  pass: "Pass",
  near_miss: "Near Miss",
  fail: "Fail",
} as const;

export const PSA_ROOT_CAUSE_LABELS: Record<string, string> = {
  capacity_constraint: "Capacity Constraint",
  material_shortage: "Material Shortage",
  qc_hold: "QC Hold",
  equipment_failure: "Equipment Failure",
  operator_shortage: "Operator Shortage",
  rescheduled_upstream: "Rescheduled Upstream",
  other: "Other",
} as const;

// ── NCR Severity Level ──────────────────────────────────────────────────────

export const NCR_SEVERITY_LEVEL_COLORS: Record<string, string> = {
  critical: "bg-destructive/15 text-destructive",
  major: "bg-warning/15 text-warning",
  minor: "bg-muted text-muted-foreground",
} as const;

export const NCR_SEVERITY_LEVEL_LABELS: Record<string, string> = {
  critical: "Critical",
  major: "Major",
  minor: "Minor",
} as const;

// ── Time Log Activity Type ──────────────────────────────────────────────────

export const TIME_LOG_ACTIVITY_COLORS: Record<string, string> = {
  production: "bg-success/15 text-success",
  setup: "bg-primary/15 text-primary",
  maintenance: "bg-warning/15 text-warning",
  quality: "bg-amber-100 text-amber-700",
  changeover: "bg-info/15 text-info",
} as const;

export const TIME_LOG_ACTIVITY_LABELS: Record<string, string> = {
  production: "Production",
  setup: "Setup",
  maintenance: "Maintenance",
  quality: "Quality Check",
  changeover: "Changeover",
} as const;

// ── QA Check Result ─────────────────────────────────────────────────────────

export const QA_RESULT_COLORS: Record<string, string> = {
  passed: "bg-success/15 text-success",
  failed: "bg-destructive/15 text-destructive",
  pending: "bg-warning/15 text-warning",
} as const;

export const QA_RESULT_LABELS: Record<string, string> = {
  passed: "Passed",
  failed: "Failed",
  pending: "Pending",
} as const;

// ── Customer Order ─────────────────────────────────────────────────────────

export const CO_STATUS_COLORS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  draft: "bg-muted text-muted-foreground",
  confirmed: "bg-primary/15 text-primary",
  in_production: "bg-warning/15 text-warning",
  ready: "bg-info/15 text-info",
  shipped: "bg-success/15 text-success",
  completed: "bg-success/20 text-success",
  cancelled: "bg-destructive/15 text-destructive",
} as const;

export const CO_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  draft: "Draft",
  confirmed: "Confirmed",
  in_production: "In Production",
  ready: "Ready to Ship",
  shipped: "Shipped",
  completed: "Completed",
  cancelled: "Cancelled",
} as const;

export const CO_PRIORITY_COLORS: Record<string, string> = {
  low: "bg-muted text-muted-foreground",
  normal: "bg-primary/15 text-primary",
  high: "bg-warning/15 text-warning",
  urgent: "bg-destructive/15 text-destructive",
} as const;

export const CO_PRIORITY_LABELS: Record<string, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
} as const;

export const CO_PAYMENT_STATUS_COLORS: Record<string, string> = {
  unpaid: "bg-muted text-muted-foreground",
  partial: "bg-warning/15 text-warning",
  paid: "bg-success/15 text-success",
  refunded: "bg-destructive/15 text-destructive",
} as const;

export const CO_PAYMENT_STATUS_LABELS: Record<string, string> = {
  unpaid: "Unpaid",
  partial: "Partial",
  paid: "Paid",
  refunded: "Refunded",
} as const;

// Brand-aligned badge styles (explicit Tailwind colors for redesigned CRM UI)
export const CO_STATUS_BADGE_STYLES: Record<string, string> = {
  draft: "bg-slate-100 text-slate-600",
  pending: "bg-slate-100 text-slate-600",
  confirmed: "bg-blue-50 text-blue-600",
  in_production: "bg-teal-50 text-teal-600",
  ready: "bg-violet-50 text-violet-600",
  shipped: "bg-green-50 text-green-600",
  completed: "bg-green-50 text-green-600",
  cancelled: "bg-red-50 text-red-500",
} as const;

export const CO_PRIORITY_BADGE_STYLES: Record<string, string> = {
  low: "bg-slate-100 text-slate-500",
  normal: "bg-slate-100 text-slate-600",
  high: "bg-red-50 text-red-600",
  urgent: "bg-red-100 text-red-700",
} as const;

// ── Invoice ────────────────────────────────────────────────────────────────

export const INVOICE_STATUS_COLORS: Record<string, string> = {
  draft: "bg-slate-100 text-slate-600",
  sent: "bg-blue-50 text-blue-600",
  paid: "bg-green-50 text-green-600",
  partial: "bg-amber-50 text-amber-600",
  overdue: "bg-red-50 text-red-600",
  void: "bg-slate-100 text-slate-400",
} as const;

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  paid: "Paid",
  partial: "Partial",
  overdue: "Overdue",
  void: "Void",
} as const;

export const INVOICE_TYPE_COLORS: Record<string, string> = {
  invoice: "bg-blue-50 text-blue-600",
  pre_payment: "bg-teal-50 text-teal-600",
  credit_note: "bg-amber-50 text-amber-600",
} as const;

export const INVOICE_TYPE_LABELS: Record<string, string> = {
  invoice: "Invoice",
  pre_payment: "Pre-payment",
  credit_note: "Credit Note",
} as const;

export const CO_INVOICE_STATUS_COLORS: Record<string, string> = {
  uninvoiced: "bg-slate-100 text-slate-500",
  partial: "bg-amber-50 text-amber-600",
  invoiced: "bg-green-50 text-green-600",
} as const;

export const CO_INVOICE_STATUS_LABELS: Record<string, string> = {
  uninvoiced: "Uninvoiced",
  partial: "Partially Invoiced",
  invoiced: "Invoiced",
} as const;

// ── Fulfillment ────────────────────────────────────────────────────────────

export const FULFILLMENT_STATUS_COLORS: Record<string, string> = {
  unfulfilled: "bg-slate-100 text-slate-500",
  partial: "bg-amber-50 text-amber-600",
  fulfilled: "bg-green-50 text-green-600",
  closed_short: "bg-red-50 text-red-500",
} as const;

export const FULFILLMENT_STATUS_LABELS: Record<string, string> = {
  unfulfilled: "Unfulfilled",
  partial: "Partial",
  fulfilled: "Fulfilled",
  closed_short: "Closed Short",
} as const;

export const DELIVERY_STATUS_COLORS: Record<string, string> = {
  pending: "bg-slate-100 text-slate-500",
  on_time: "bg-green-50 text-green-600",
  at_risk: "bg-amber-50 text-amber-600",
  delayed: "bg-red-50 text-red-600",
} as const;

export const DELIVERY_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  on_time: "On Time",
  at_risk: "At Risk",
  delayed: "Delayed",
} as const;

// ── Purchase Order ──────────────────────────────────────────────────────────

export const PO_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  submitted: "bg-primary/15 text-primary",
  approved: "bg-info/15 text-info",
  ordered: "bg-primary/15 text-primary",
  partially_received: "bg-warning/15 text-warning",
  received: "bg-success/15 text-success",
  closed: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/15 text-destructive",
  pending: "bg-warning/15 text-warning",
  rfq: "bg-muted text-muted-foreground",
} as const;

export const PO_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved",
  ordered: "Ordered",
  partially_received: "Partially Received",
  received: "Received",
  closed: "Closed",
  cancelled: "Cancelled",
  pending: "Pending",
  rfq: "RFQ",
} as const;

// ── Consumables Ordering & Consolidation module ─────────────────────────────
//
// TODO(tech-debt): migrate these to DB-sourced status maps; the iOS
// StatusMaps.swift mirror is INTENTIONALLY NOT extended — this module is
// web-only in v1 (iOS Purchasing is viewer-only). See brief §0.6 / §4.3.

// The 9 internal consumable_requests.status values (PM-facing surfaces).
export const CONSUMABLE_REQUEST_STATUS_LABELS: Record<string, string> = {
  triage: "Triage",
  pending_batch: "Pending Batch",
  urgent_review: "Urgent Review",
  in_draft_po: "In Draft PO",
  ordered: "Ordered",
  received: "Received",
  collected: "Collected",
  declined: "Declined",
  cancelled: "Cancelled",
} as const;

export const CONSUMABLE_REQUEST_STATUS_COLORS: Record<string, string> = {
  triage: "bg-purple-100 text-purple-700 border-purple-200",
  pending_batch: "bg-muted text-muted-foreground",
  urgent_review: "bg-destructive/15 text-destructive",
  in_draft_po: "bg-info/15 text-info",
  ordered: "bg-primary/15 text-primary",
  received: "bg-warning/15 text-warning",
  collected: "bg-success/15 text-success",
  declined: "bg-destructive/10 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
} as const;

// The 4 collapsed statuses shown to requesters (brief §4.2). Maps each internal
// status to one of: requested / ordered / arrived / done (declined+cancelled are
// shown in a separate collapsed history section, not in this primary timeline).
export const CONSUMABLE_REQUESTER_STATUS_LABELS: Record<string, string> = {
  requested: "Requested",
  ordered: "Ordered",
  arrived: "Arrived",
  done: "Done",
} as const;

export const CONSUMABLE_REQUESTER_STATUS_COLORS: Record<string, string> = {
  requested: "bg-muted text-muted-foreground",
  ordered: "bg-primary/15 text-primary",
  arrived: "bg-warning/15 text-warning",
  done: "bg-success/15 text-success",
} as const;

// Maps an internal request status → its collapsed requester-facing bucket.
// declined / cancelled return null (rendered in the history section instead).
export const CONSUMABLE_REQUESTER_STATUS_OF: Record<string, "requested" | "ordered" | "arrived" | "done" | null> = {
  triage: "requested",
  pending_batch: "requested",
  urgent_review: "requested",
  in_draft_po: "requested",
  ordered: "ordered",
  received: "arrived",
  collected: "done",
  declined: null,
  cancelled: null,
} as const;

// purchase_orders.origin provenance (brief §2.5).
export const PO_ORIGIN_LABELS: Record<string, string> = {
  manual: "Manual",
  mrp: "MRP",
  consumables_batch: "Consolidated Batch",
  consumables_urgent: "Urgent Consumable",
} as const;

export const PO_ORIGIN_COLORS: Record<string, string> = {
  manual: "bg-muted text-muted-foreground",
  mrp: "bg-info/15 text-info",
  consumables_batch: "bg-primary/15 text-primary",
  consumables_urgent: "bg-destructive/15 text-destructive",
} as const;

// Batch disposition (the PM's send/defer decision + the greyed recommendation).
export const BATCH_DISPOSITION_LABELS: Record<string, string> = {
  send: "Send",
  defer: "Defer",
} as const;

export const BATCH_DISPOSITION_COLORS: Record<string, string> = {
  send: "bg-success/15 text-success",
  defer: "bg-warning/15 text-warning",
} as const;

export const PAYMENT_STATUS_COLORS: Record<string, string> = {
  pending: "bg-warning/15 text-warning",
  approved: "bg-info/15 text-info",
  paid: "bg-success/15 text-success",
  confirmed: "bg-success/15 text-success",
} as const;

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  paid: "Paid",
  confirmed: "Confirmed",
} as const;

// ── Stocktake ───────────────────────────────────────────────────────────────

export const STOCKTAKE_STATUS_COLORS: Record<string, string> = {
  planned: "bg-muted text-muted-foreground",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-primary/15 text-primary",
  approved: "bg-success/15 text-success",
  cancelled: "bg-destructive/15 text-destructive",
} as const;

export const STOCKTAKE_STATUS_LABELS: Record<string, string> = {
  planned: "Planned",
  in_progress: "In Progress",
  completed: "Completed",
  approved: "Approved",
  cancelled: "Cancelled",
} as const;

export const STOCKTAKE_TYPE_LABELS: Record<string, string> = {
  full: "Full",
  cycle: "Cycle",
  spot: "Spot",
} as const;

// ── Pick Tasks ─────────────────────────────────────────────────────────────

export const PICK_TASK_STATUS_COLORS: Record<string, string> = {
  pending: "bg-warning/15 text-warning",
  in_progress: "bg-info/15 text-info",
  picked: "bg-success/15 text-success",
  short_picked: "bg-amber-100 text-amber-700",
  cancelled: "bg-muted text-muted-foreground",
  overridden: "bg-orange-100 text-orange-700",
} as const;

export const PICK_TASK_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  picked: "Picked",
  short_picked: "Short Pick",
  cancelled: "Cancelled",
  overridden: "Overridden",
} as const;

// ── Putaway Tasks ─────────────────────────────────────────────────────────

export const PUTAWAY_TASK_STATUS_COLORS: Record<string, string> = {
  pending: "bg-warning/15 text-warning",
  in_progress: "bg-info/15 text-info",
  completed: "bg-success/15 text-success",
  cancelled: "bg-muted text-muted-foreground",
  overridden: "bg-amber-100 text-amber-700",
} as const;

export const PUTAWAY_TASK_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
  overridden: "Overridden",
} as const;

// ── Recall Investigations ─────────────────────────────────────────────────

export const RECALL_STATUS_COLORS: Record<string, string> = {
  draft:     "bg-muted text-muted-foreground",
  active:    "bg-destructive/20 text-destructive",
  contained: "bg-warning/20 text-warning-foreground",
  closed:    "bg-success/20 text-success",
  cancelled: "bg-muted text-muted-foreground",
} as const;

export const RECALL_STATUS_LABELS: Record<string, string> = {
  draft:     "Draft",
  active:    "Active",
  contained: "Contained",
  closed:    "Closed",
  cancelled: "Cancelled",
} as const;

export const RECALL_TYPE_LABELS: Record<string, string> = {
  voluntary_recall:  "Voluntary Recall",
  mandatory_recall:  "Mandatory Recall",
  market_withdrawal: "Market Withdrawal",
  stock_recovery:    "Stock Recovery",
} as const;

export const RECALL_TRIGGER_LABELS: Record<string, string> = {
  allergen_undeclared:              "Undeclared Allergen",
  contamination_physical:           "Physical Contamination",
  contamination_chemical:           "Chemical Contamination",
  contamination_microbiological:    "Microbiological Contamination",
  label_error:                      "Label Error",
  formulation_error:                "Formulation Error",
  supplier_recall:                  "Supplier Recall",
  regulatory_action:                "Regulatory Action",
  other:                            "Other",
} as const;

export const RECALL_NOTIFICATION_METHOD_LABELS: Record<string, string> = {
  email:     "Email",
  phone:     "Phone",
  in_person: "In Person",
  mail:      "Mail",
  portal:    "Portal",
} as const;

export const RECALL_IMPACT_TYPE_LABELS: Record<string, string> = {
  trigger_lot:                   "Trigger Lot",
  finished_goods_from_component: "Finished Goods",
  same_batch_component:          "Co-Ingredient",
  manually_added:                "Manually Added",
} as const;

// ── Inventory Accuracy (Phase 1H) ────────────────────────────────────────────

export const ITEM_ABC_CLASS_LABELS: Record<string, string> = {
  A: "High Value / Fast-Moving",
  B: "Medium Value",
  C: "Low Value / Slow-Moving",
} as const;

export const ITEM_ABC_CLASS_COLORS: Record<string, string> = {
  A: "bg-destructive/20 text-destructive border-destructive/30",
  B: "bg-warning/20 text-warning-foreground border-warning/30",
  C: "bg-muted text-muted-foreground",
} as const;

export const LOCATION_STATUS_LABELS: Record<string, string> = {
  active:             "Active",
  count_in_progress:  "Count In Progress",
  locked:             "Locked",
} as const;

export const LOCATION_STATUS_COLORS: Record<string, string> = {
  active:             "bg-success/20 text-success",
  count_in_progress:  "bg-warning/20 text-warning-foreground",
  locked:             "bg-destructive/20 text-destructive",
} as const;

// ── Costing Method ────────────────────────────────────────────────────────────

export const COSTING_METHOD_LABELS: Record<string, string> = {
  moving_average: "Moving Average",
  standard_cost:  "Standard Cost",
  fifo:           "FIFO",
} as const;

export const COSTING_METHOD_COLORS: Record<string, string> = {
  moving_average: "bg-primary/15 text-primary",
  standard_cost:  "bg-muted text-muted-foreground",
  fifo:           "bg-success/15 text-success",
} as const;

// ── Pack Session Status ───────────────────────────────────────────────────────

export const PACK_SESSION_STATUS_LABELS: Record<string, string> = {
  in_progress: "In Progress",
  packed:      "Packed",
  cancelled:   "Cancelled",
} as const;

export const PACK_SESSION_STATUS_COLORS: Record<string, string> = {
  in_progress: "bg-warning/20 text-warning-foreground",
  packed:      "bg-success/20 text-success",
  cancelled:   "bg-muted text-muted-foreground",
} as const;

export const PACK_LINE_STATUS_LABELS: Record<string, string> = {
  pending:   "Pending",
  scanning:  "Scanning",
  packed:    "Packed",
  mismatch:  "Mismatch",
} as const;

export const PACK_LINE_STATUS_COLORS: Record<string, string> = {
  pending:   "bg-muted text-muted-foreground",
  scanning:  "bg-primary/15 text-primary",
  packed:    "bg-success/20 text-success",
  mismatch:  "bg-destructive/20 text-destructive",
} as const;


// ── Capacity Utilization ───────────────────────────────────────────────────

/**
 * Returns the badge class for a utilization percentage.
 * >100% = over-capacity (red), >80% = high-load warning (amber), ≤80% = healthy (green).
 * Thresholds align with standard manufacturing capacity planning guidance.
 */
export function getUtilizationColor(utilization: number): string {
  if (utilization > 100) return "bg-destructive/15 text-destructive";
  if (utilization > 80) return "bg-warning/15 text-warning";
  return "bg-success/15 text-success";
}

// ── APEX Planning Module ─────────────────────────────────────────────────────

// Extended MO statuses for APEX-managed manufacturing orders (apex_managed = true)
// Flow: draft → feasibility_check → planned/pending_review → confirmed → in_progress → completed
// Any non-terminal status → cancelled. Transition rules enforced by DB trigger.
export const APEX_MO_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  feasibility_check: "bg-amber-100 text-amber-700",
  pending_review: "bg-destructive/15 text-destructive",
  planned: "bg-primary/10 text-primary",
  confirmed: "bg-primary/15 text-primary",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
  cancelled: "bg-muted text-muted-foreground",
} as const;

export const APEX_MO_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  feasibility_check: "Feasibility Check",
  pending_review: "Pending Review",
  planned: "Planned",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
} as const;

// Valid APEX MO status transitions (mirrors DB trigger)
export const APEX_MO_STATUS_TRANSITIONS: Record<string, string[]> = {
  draft: ["feasibility_check", "cancelled"],
  feasibility_check: ["planned", "pending_review", "cancelled"],
  pending_review: ["feasibility_check", "planned", "cancelled"],
  planned: ["confirmed", "cancelled"],
  confirmed: ["in_progress", "cancelled"],
  in_progress: ["completed", "cancelled"],
} as const;

// Supply pipeline 5-stage model (ordered → shipped → customs → received → available)
export const SUPPLY_PIPELINE_STAGE_COLORS: Record<string, string> = {
  ordered: "bg-muted text-muted-foreground",
  shipped: "bg-primary/15 text-primary",
  customs: "bg-amber-100 text-amber-700",
  received: "bg-info/15 text-info",
  available: "bg-success/15 text-success",
} as const;

export const SUPPLY_PIPELINE_STAGE_LABELS: Record<string, string> = {
  ordered: "Ordered",
  shipped: "In Transit",
  customs: "Customs / Clearance",
  received: "Received at Factory",
  available: "Available for Use",
} as const;

// Cashflow signal (immutable audit log — affects purchasing decisions)
export const CASHFLOW_SIGNAL_COLORS: Record<string, string> = {
  GREEN: "bg-success/15 text-success",
  AMBER: "bg-amber-100 text-amber-700",
  RED: "bg-destructive/15 text-destructive",
} as const;

export const CASHFLOW_SIGNAL_LABELS: Record<string, string> = {
  GREEN: "Normal Operations",
  AMBER: "Caution — Hold Large Prepaid",
  RED: "Critical — Essential Only",
} as const;

// Feasibility gate results (5-check system)
export const FEASIBILITY_RESULT_COLORS: Record<string, string> = {
  pass: "bg-success/15 text-success",
  fail: "bg-destructive/15 text-destructive",
  warning: "bg-warning/15 text-warning",
  not_checked: "bg-muted text-muted-foreground",
} as const;

export const FEASIBILITY_RESULT_LABELS: Record<string, string> = {
  pass: "Pass",
  fail: "Fail",
  warning: "Warning",
  not_checked: "Not Checked",
} as const;

// Demand register sources
export const DEMAND_SOURCE_COLORS: Record<string, string> = {
  customer_order: "bg-primary/15 text-primary",
  manufacturing_order: "bg-warning/15 text-warning",
  reorder_point: "bg-info/15 text-info",
  manual_forecast: "bg-muted text-muted-foreground",
} as const;

export const DEMAND_SOURCE_LABELS: Record<string, string> = {
  customer_order: "Customer Order",
  manufacturing_order: "Manufacturing Order",
  reorder_point: "Reorder Point",
  manual_forecast: "Manual Forecast",
} as const;

// Ingredient substitution tiers
export const SUBSTITUTION_TIER_COLORS: Record<string, string> = {
  tier_1: "bg-success/15 text-success",
  tier_2: "bg-warning/15 text-warning",
} as const;

export const SUBSTITUTION_TIER_LABELS: Record<string, string> = {
  tier_1: "Tier 1 — Auto-Apply",
  tier_2: "Tier 2 — QA Hold Required",
} as const;

// Scrap factor version status
export const SCRAP_FACTOR_STATUS_COLORS: Record<string, string> = {
  active: "bg-success/15 text-success",
  pending: "bg-warning/15 text-warning",
  locked: "bg-muted text-muted-foreground",
} as const;

export const SCRAP_FACTOR_STATUS_LABELS: Record<string, string> = {
  active: "Active",
  pending: "Pending Activation",
  locked: "Period Locked",
} as const;

// MRP requirement actions
// 'rejected' is a HUMAN decision; 'superseded' is a later engine run replacing
// the row. They were the same value until 2026-07-23, which made planner-reject
// analytics meaningless. Keep them visually distinct: rejected is a signal worth
// noticing, superseded is routine churn (every run supersedes the whole open set).
// Mirrored in ios/Operations App/Utilities/StatusMaps.swift and the
// mrp_requirements action_status CHECK (migration 20280191000000).
export const MRP_ACTION_STATUS_COLORS: Record<string, string> = {
  suggested: "bg-muted text-muted-foreground",
  confirmed: "bg-primary/15 text-primary",
  rejected: "bg-destructive/15 text-destructive",
  actioned: "bg-success/15 text-success",
  superseded: "bg-muted/60 text-muted-foreground",
} as const;

export const MRP_ACTION_STATUS_LABELS: Record<string, string> = {
  suggested: "Suggested",
  confirmed: "Confirmed",
  rejected: "Rejected",
  actioned: "Actioned",
  superseded: "Superseded",
} as const;

// Delivery delay reason codes (DIFOT root cause attribution)
export const DELAY_REASON_LABELS: Record<string, string> = {
  INGREDIENT_SHORTAGE: "Ingredient Shortage",
  CAPACITY_CONSTRAINT: "Capacity Constraint",
  CASHFLOW_HOLD: "Cashflow Hold",
  QUALITY_HOLD: "Quality Hold",
  CUSTOMER_CHANGE: "Customer Change",
  SUPPLIER_DELAY: "Supplier Delay",
  STAFF_SHORTAGE: "Staff Shortage",
  EQUIPMENT_FAILURE: "Equipment Failure",
  OTHER: "Other",
} as const;

export const DELAY_REASON_CONTROLLABLE: Record<string, boolean> = {
  INGREDIENT_SHORTAGE: true,
  CAPACITY_CONSTRAINT: true,
  CASHFLOW_HOLD: true,
  QUALITY_HOLD: true,
  CUSTOMER_CHANGE: false,
  SUPPLIER_DELAY: false,
  STAFF_SHORTAGE: true,
  EQUIPMENT_FAILURE: true,
  OTHER: false,
} as const;

// ── NPD Pipeline Stages (unified pipeline) ──────────────────────────────────

export const NPD_PHASE_COLORS: Record<string, string> = {
  // New pipeline stage keys
  quoting: "bg-cyan-100 text-cyan-700 border-cyan-200",
  development: "bg-violet-100 text-violet-700 border-violet-200",
  quality_signoff: "bg-emerald-100 text-emerald-700 border-emerald-200",
  production_setup: "bg-orange-100 text-orange-700 border-orange-200",
  released: "bg-slate-100 text-slate-700 border-slate-200",
  archived: "bg-gray-100 text-gray-500 border-gray-200",
  // Legacy aliases for backward compatibility
  scoping: "bg-cyan-100 text-cyan-700 border-cyan-200",
  production_ready: "bg-orange-100 text-orange-700 border-orange-200",
  qa_review: "bg-emerald-100 text-emerald-700 border-emerald-200",
  formula_dev: "bg-violet-100 text-violet-700 border-violet-200",
  sampling: "bg-violet-100 text-violet-700 border-violet-200",
  preliminary_quote: "bg-cyan-100 text-cyan-700 border-cyan-200",
  client_decision: "bg-cyan-100 text-cyan-700 border-cyan-200",
  declined: "bg-gray-100 text-gray-500 border-gray-200",
} as const;

export const NPD_PHASE_LABELS: Record<string, string> = {
  // V2 pipeline stage keys (PHASE_CONFIG labels). These were missing, so
  // ArchiveList / useArchiveFormulation fell through to the raw key for any
  // formulation archived from a V2 stage. iOS StatusMaps.swift mirrors this map.
  quoting: "Quoting",
  development: "Development",
  compliance: "Compliance",
  artwork_proofing: "Artwork & Proofing",
  qa_signoff: "QA Sign-off",
  released: "Released",
  archived: "Archived",
  // V1 pipeline stage keys (still allowed by the CHECK constraint)
  quality_signoff: "Quality & Sign-off",
  production_setup: "Production Setup",
  // Legacy aliases
  scoping: "Quoting",
  production_ready: "Production Setup",
  qa_review: "Quality & Sign-off",
  formula_dev: "Development",
  sampling: "Development",
  preliminary_quote: "Quoting",
  client_decision: "Quoting",
  declined: "Archived",
} as const;

/** Hex color tokens for pipeline stage visualizations (from brief Section 2.1) */
export const NPD_PIPELINE_STAGE_COLORS: Record<string, string> = {
  quoting: "#0BBBD5",
  development: "#7C3AED",
  quality_signoff: "#059669",
  production_setup: "#EA580C",
  released: "#374151",
  archived: "#6B7280",
} as const;

export const NPD_PRIORITY_COLORS: Record<string, string> = {
  low: "bg-muted text-muted-foreground",
  normal: "bg-sky-100 text-sky-700 border-sky-200",
  medium: "bg-primary/15 text-primary",
  high: "bg-warning/15 text-warning",
  urgent: "bg-destructive/15 text-destructive",
} as const;

export const NPD_PRIORITY_LABELS: Record<string, string> = {
  low: "Low",
  normal: "Normal",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
} as const;

// ── NPD Health ──────────────────────────────────────────────────────────────

export type HealthStatus = "on_track" | "at_risk" | "blocked" | "stalled";

export const NPD_HEALTH_COLORS: Record<HealthStatus, string> = {
  on_track: "bg-success/15 text-success",
  at_risk: "bg-warning/15 text-warning",
  blocked: "bg-destructive/15 text-destructive",
  stalled: "bg-muted text-muted-foreground",
} as const;

export const NPD_HEALTH_LABELS: Record<HealthStatus, string> = {
  on_track: "On Track",
  at_risk: "At Risk",
  blocked: "Blocked",
  stalled: "Stalled",
} as const;

/** Icon names (from lucide-react) for each health status */
export const NPD_HEALTH_ICONS: Record<HealthStatus, string> = {
  on_track: "CheckCircle2",
  at_risk: "AlertTriangle",
  blocked: "XCircle",
  stalled: "Pause",
} as const;

/** Dot color classes for inline health indicators (pipeline charts, table badges) */
export const NPD_HEALTH_DOT_COLORS: Record<HealthStatus, string> = {
  on_track: "bg-green-500",
  at_risk: "bg-amber-500",
  blocked: "bg-red-500",
  stalled: "bg-gray-400",
} as const;

// ── NPD Task Category ──────────────────────────────────────────────────────

export const NPD_TASK_CATEGORY_LABELS: Record<string, string> = {
  scoping: "Scoping",
  formulation: "Formulation",
  sampling: "Sampling",
  packaging: "Packaging",
  qa: "QA",
  pilot: "Pilot",
  general: "General",
} as const;

export const NPD_TASK_CATEGORY_COLORS: Record<string, string> = {
  scoping: "bg-amber-100 text-amber-700 border-amber-200",
  formulation: "bg-blue-100 text-blue-700 border-blue-200",
  sampling: "bg-purple-100 text-purple-700 border-purple-200",
  packaging: "bg-emerald-100 text-emerald-700 border-emerald-200",
  qa: "bg-red-100 text-red-700 border-red-200",
  pilot: "bg-cyan-100 text-cyan-700 border-cyan-200",
  general: "bg-gray-100 text-gray-700 border-gray-200",
} as const;

// ── NPD Task Group ─────────────────────────────────────────────────────────

export const NPD_TASK_GROUP_LABELS: Record<string, string> = {
  documents: "Documents",
  artwork_customer: "Artwork & Customer",
  production_prep: "Production Prep",
} as const;

// ── Task Alerts ─────────────────────────────────────────────────────────────

export const TASK_ALERT_RESOLUTION_COLORS: Record<string, string> = {
  done: "bg-success/15 text-success",
  declined: "bg-destructive/15 text-destructive",
  reassigned: "bg-warning/15 text-warning",
} as const;

export const TASK_ALERT_RESOLUTION_LABELS: Record<string, string> = {
  done: "Done",
  declined: "Declined",
  reassigned: "Reassigned",
} as const;

export const TASK_ALERT_PRIORITY_COLORS: Record<string, string> = {
  normal: "bg-muted text-muted-foreground",
  low: "bg-muted text-muted-foreground",
  urgent: "bg-destructive/15 text-destructive",
  high: "bg-destructive/15 text-destructive",
} as const;

export const TASK_ALERT_PRIORITY_LABELS: Record<string, string> = {
  low: "Low",
  normal: "Normal",
  urgent: "Urgent",
  high: "Urgent",
} as const;

export const TASK_DECLINE_REASON_LABELS: Record<string, string> = {
  not_relevant: "Not Relevant",
  wrong_person: "Wrong Person",
  insufficient_info: "Insufficient Info",
  already_done: "Already Done",
  other: "Other",
} as const;

export const TASK_CONTEXT_TYPE_LABELS: Record<string, string> = {
  formulation: "Formulation",
  purchase_order: "Purchase Order",
  manufacturing_order: "Manufacturing Order",
  ncr: "NCR",
  sop: "SOP",
} as const;

// ── NPD Activity Status (per-stage sub-status with burden indicator) ─────────

export type BurdenType = "internal" | "customer" | "external";

export interface ActivityStatusOption {
  value: string;
  label: string;
  burden: BurdenType;
}

export const NPD_ACTIVITY_STATUS_OPTIONS: Record<string, ActivityStatusOption[]> = {
  quoting: [
    { value: "quote_in_progress", label: "In progress", burden: "internal" },
    { value: "awaiting_customer_review", label: "Awaiting customer review", burden: "customer" },
    { value: "customer_reviewed_rework_required", label: "Customer reviewed — rework required", burden: "internal" },
    { value: "quote_customer_approved", label: "Customer approved", burden: "internal" },
    // Backward-compatible legacy quoting statuses
    { value: "brief_review", label: "Brief under review", burden: "internal" },
    { value: "awaiting_client_info", label: "Awaiting client information", burden: "customer" },
    { value: "feasibility_assessment", label: "Feasibility assessment", burden: "internal" },
    { value: "quote_preparation", label: "Quote preparation", burden: "internal" },
    { value: "awaiting_quote_approval", label: "Awaiting quote approval", burden: "customer" },
  ],
  // Legacy quoting keys kept so old callers still resolve options.
  scoping: [],
  preliminary_quote: [],
  client_decision: [],
  development: [
    { value: "actionable_sampling_required", label: "Actionable — sampling required", burden: "internal" },
    { value: "actionable_flavour_development_required", label: "Actionable — flavour development required", burden: "internal" },
    { value: "samples_sent", label: "Samples sent — awaiting customer feedback", burden: "customer" },
    { value: "customer_feedback_received_revisions_required", label: "Customer feedback received — revisions required", burden: "internal" },
    { value: "development_customer_approved", label: "Customer approved", burden: "internal" },
    { value: "stalled_waiting_supply_ordered", label: "Stalled — ordered, awaiting ingredients/samples/supplier items", burden: "external" },
    { value: "stalled_waiting_supply_unconfirmed", label: "Stalled — supply/order not confirmed", burden: "internal" },
    // Backward-compatible legacy development statuses
    { value: "formulation_in_progress", label: "Formulation in progress", burden: "internal" },
    { value: "bench_samples_pending", label: "Awaiting bench samples", burden: "internal" },
    { value: "awaiting_client_feedback", label: "Awaiting client feedback", burden: "customer" },
    { value: "formula_iteration", label: "Formula iteration in progress", burden: "internal" },
    { value: "stability_testing", label: "Stability testing", burden: "internal" },
    { value: "ready_for_signoff", label: "Ready for sign-off", burden: "customer" },
  ],
  compliance: [
    { value: "testing_in_progress", label: "Testing in progress", burden: "internal" },
    { value: "awaiting_lab_results", label: "Awaiting lab results", burden: "external" },
    { value: "label_compliance", label: "Label compliance review", burden: "internal" },
    { value: "awaiting_labels_review", label: "Awaiting client labels for review", burden: "customer" },
    { value: "awaiting_regulatory", label: "Awaiting regulatory approval", burden: "external" },
    { value: "final_review", label: "Final review pending", burden: "internal" },
  ],
  artwork_proofing: [
    { value: "pa_entry", label: "Product entry in progress", burden: "internal" },
    { value: "awaiting_artwork", label: "Awaiting artwork from client", burden: "customer" },
    { value: "artwork_review", label: "Artwork under review", burden: "internal" },
    { value: "awaiting_client_labels", label: "Awaiting client labels", burden: "customer" },
    { value: "fps_dispatch", label: "FPS dispatch pending", burden: "internal" },
    { value: "awaiting_fps_approval", label: "Awaiting FPS approval", burden: "customer" },
    { value: "scaleup_scheduling", label: "Scale-up scheduling", burden: "internal" },
  ],
  qa_signoff: [],
  // Legacy phase aliases
  production_ready: [],
  qa_review: [],
  released: [
    { value: "awaiting_po", label: "Awaiting purchase order", burden: "customer" },
    { value: "materials_procurement", label: "Materials procurement", burden: "internal" },
    { value: "production_scheduling", label: "Production scheduling", burden: "internal" },
    { value: "in_production", label: "In production", burden: "internal" },
    { value: "complete", label: "Complete", burden: "internal" },
  ],
} as const;

NPD_ACTIVITY_STATUS_OPTIONS.scoping = NPD_ACTIVITY_STATUS_OPTIONS.quoting;
NPD_ACTIVITY_STATUS_OPTIONS.preliminary_quote = NPD_ACTIVITY_STATUS_OPTIONS.quoting;
NPD_ACTIVITY_STATUS_OPTIONS.client_decision = NPD_ACTIVITY_STATUS_OPTIONS.quoting;
NPD_ACTIVITY_STATUS_OPTIONS.production_ready = NPD_ACTIVITY_STATUS_OPTIONS.artwork_proofing;
NPD_ACTIVITY_STATUS_OPTIONS.qa_review = NPD_ACTIVITY_STATUS_OPTIONS.compliance;
NPD_ACTIVITY_STATUS_OPTIONS.qa_signoff = NPD_ACTIVITY_STATUS_OPTIONS.compliance;

/** Flat lookup: activity_status value → display label */
export const NPD_ACTIVITY_STATUS_LABELS: Record<string, string> = Object.values(
  NPD_ACTIVITY_STATUS_OPTIONS
).flat().reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {} as Record<string, string>);

export const NPD_DEV_KANBAN_STATUS_ORDER = [
  "actionable_sampling_required",
  "actionable_flavour_development_required",
  "samples_sent",
  "customer_feedback_received_revisions_required",
  "development_customer_approved",
  "stalled_waiting_supply_ordered",
  "stalled_waiting_supply_unconfirmed",
] as const;

export const NPD_QUOTE_KANBAN_STATUS_ORDER = [
  "quote_in_progress",
  "awaiting_customer_review",
  "customer_reviewed_rework_required",
  "quote_customer_approved",
] as const;

export const NPD_KANBAN_STATUS_PILL_CLASSES: Record<string, string> = {
  actionable_sampling_required: "bg-green-100 text-green-700 border-green-200",
  actionable_flavour_development_required: "bg-emerald-100 text-emerald-700 border-emerald-200",
  samples_sent: "bg-amber-100 text-amber-700 border-amber-200",
  customer_feedback_received_revisions_required: "bg-orange-100 text-orange-700 border-orange-200",
  development_customer_approved: "bg-teal-100 text-teal-700 border-teal-200",
  stalled_waiting_supply_ordered: "bg-yellow-100 text-yellow-800 border-yellow-300",
  stalled_waiting_supply_unconfirmed: "bg-red-100 text-red-700 border-red-300",
  quote_in_progress: "bg-blue-100 text-blue-700 border-blue-200",
  awaiting_customer_review: "bg-amber-100 text-amber-700 border-amber-200",
  customer_reviewed_rework_required: "bg-orange-100 text-orange-700 border-orange-200",
  quote_customer_approved: "bg-teal-100 text-teal-700 border-teal-200",
};

/** Flat lookup: activity_status value → burden type */
export const NPD_ACTIVITY_STATUS_BURDEN: Record<string, BurdenType> = Object.values(
  NPD_ACTIVITY_STATUS_OPTIONS
).flat().reduce((acc, opt) => {
  acc[opt.value] = opt.burden;
  return acc;
}, {} as Record<string, BurdenType>);

/**
 * Explicit action_state overrides for status values that should be treated as
 * stalled regardless of burden category.
 */
export const NPD_ACTIVITY_STATUS_ACTION_STATE_OVERRIDES: Record<string, "action" | "awaiting" | "stalled"> = {
  stalled_waiting_supply_ordered: "stalled",
  stalled_waiting_supply_unconfirmed: "stalled",
};

export const NPD_BURDEN_COLORS: Record<BurdenType, string> = {
  internal: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  customer: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  external: "bg-purple-500/15 text-purple-700 dark:text-purple-400",
} as const;

export const NPD_BURDEN_DOT_COLORS: Record<BurdenType, string> = {
  internal: "bg-blue-500",
  customer: "bg-amber-500",
  external: "bg-purple-500",
} as const;

export const NPD_BURDEN_LABELS: Record<BurdenType, string> = {
  internal: "Internal",
  customer: "Customer",
  external: "External",
} as const;

export const NPD_BURDEN_ICONS: Record<BurdenType, ElementType> = {
  internal: Building2,
  customer: User,
  external: Globe,
} as const;

// ── NPD Pipeline Redesign — Stage Maps (extended) ────────────────────────────

/** @deprecated Use NPD_PHASE_LABELS with pipeline_stage values instead */
export const NPD_STAGE_LABELS: Record<string, string> = {
  // New pipeline stages
  quoting: "Quoting",
  development: "Development",
  quality_signoff: "Quality & Sign-off",
  production_setup: "Production Setup",
  released: "Released",
  archived: "Archived",
  // Legacy sub_phase values (kept for backward compat)
  scoping: "Quoting",
  preliminary_quote: "Preliminary Quote",
  client_decision: "Client Decision",
  formula_dev: "Formula Dev",
  sampling: "Sampling",
  qa_review: "QA Review",
  production_ready: "Production Ready",
  declined: "Declined",
};

/** @deprecated Use NPD_PHASE_COLORS with pipeline_stage values instead */
export const NPD_STAGE_COLORS: Record<string, string> = {
  // New pipeline stages
  quoting: "bg-cyan-100 text-cyan-700 border-cyan-200",
  development: "bg-violet-100 text-violet-700 border-violet-200",
  quality_signoff: "bg-emerald-100 text-emerald-700 border-emerald-200",
  production_setup: "bg-orange-100 text-orange-700 border-orange-200",
  released: "bg-slate-100 text-slate-700 border-slate-200",
  archived: "bg-gray-100 text-gray-500 border-gray-200",
  // Legacy sub_phase values
  scoping: "bg-amber-100 text-amber-700 border-amber-200",
  preliminary_quote: "bg-orange-100 text-orange-700 border-orange-200",
  client_decision: "bg-cyan-100 text-cyan-700 border-cyan-200",
  formula_dev: "bg-indigo-100 text-indigo-700 border-indigo-200",
  sampling: "bg-amber-100 text-amber-700 border-amber-200",
  qa_review: "bg-purple-100 text-purple-700 border-purple-200",
  production_ready: "bg-emerald-100 text-emerald-700 border-emerald-200",
  declined: "bg-red-100 text-red-700 border-red-200",
};

// Pipeline labels (legacy dual-pipeline, kept for backward compat)
export type NpdPipeline = "quote" | "development";

export const NPD_PIPELINE_LABELS: Record<string, string> = {
  quote: "Quote Pipeline",
  development: "Development Board",
};

// Action State — applies to all pipeline stages
export type NpdActionState = "action" | "awaiting" | "stalled" | "dormant";

export const NPD_ACTION_STATE_LABELS: Record<string, string> = {
  action: "Action Required",
  awaiting: "Awaiting",
  stalled: "Stalled",
  dormant: "Dormant",
  awaiting_label_design: "Awaiting Label Design",
};

export const NPD_ACTION_STATE_COLORS: Record<string, string> = {
  action: "bg-emerald-100 text-emerald-700 border-emerald-200",
  awaiting: "bg-amber-100 text-amber-700 border-amber-200",
  stalled: "bg-red-100 text-red-700 border-red-200",
  dormant: "bg-red-100 text-red-700 border-red-200",
  awaiting_label_design: "bg-amber-100 text-amber-700 border-amber-200",
};

/** Icon names (from lucide-react) for each action state */
export const NPD_ACTION_STATE_ICONS: Record<string, string> = {
  action: "Zap",
  awaiting: "Clock",
  stalled: "AlertTriangle",
  dormant: "Moon",
};

// ── FPS Handover Status ────────────────────────────────────────────
export const HANDOVER_STATUS_LABELS: Record<string, string> = {
  not_sent: "Not Sent",
  sent: "Sent",
  awaiting_upload: "Awaiting Label Design",
  reminder_1_sent: "Reminder Sent",
  reminder_2_sent: "Final Reminder Sent",
  internally_flagged: "Internally Flagged",
  label_received: "Label Received",
  paused: "Reminders Paused",
};

export const HANDOVER_STATUS_COLORS: Record<string, string> = {
  not_sent: "bg-gray-100 text-gray-600 border-gray-200",
  sent: "bg-blue-100 text-blue-700 border-blue-200",
  awaiting_upload: "bg-amber-100 text-amber-700 border-amber-200",
  reminder_1_sent: "bg-orange-100 text-orange-700 border-orange-200",
  reminder_2_sent: "bg-red-100 text-red-700 border-red-200",
  internally_flagged: "bg-red-200 text-red-800 border-red-300",
  label_received: "bg-emerald-100 text-emerald-700 border-emerald-200",
  paused: "bg-slate-100 text-slate-600 border-slate-200",
};

export const HANDOVER_STATUS_ICONS: Record<string, string> = {
  not_sent: "Circle",
  sent: "Send",
  awaiting_upload: "Clock",
  reminder_1_sent: "Bell",
  reminder_2_sent: "BellRing",
  internally_flagged: "AlertTriangle",
  label_received: "CheckCircle2",
  paused: "Pause",
};

// Pipeline stage column accent colors (hex) — used in board view headers
export const NPD_COLUMN_COLORS: Record<string, string> = {
  quoting: "#0BBBD5",
  development: "#7C3AED",
  quality_signoff: "#059669",
  production_setup: "#EA580C",
  released: "#374151",
  archived: "#6B7280",
};

/** @deprecated Use NPD_COLUMN_COLORS instead */
export const NPD_DEV_COLUMN_COLORS: Record<string, string> = {
  formula_dev: "#6366F1",
  sampling: "#F59E0B",
  qa_review: "#8B5CF6",
  production_ready: "#10B981",
};

// Quote Pipeline stage accent colors (hex)
export const NPD_QUOTE_STAGE_COLORS: Record<string, string> = {
  scoping: "#6366F1",
  preliminary_quote: "#F59E0B",
  client_decision: "#0BBBD5",
};

// Product type badge colors
export const NPD_PRODUCT_TYPE_COLORS: Record<string, string> = {
  powder: "bg-blue-100 text-blue-700 border-blue-200",
  liquid: "bg-sky-100 text-sky-700 border-sky-200",
  capsule: "bg-violet-100 text-violet-700 border-violet-200",
  tablet: "bg-rose-100 text-rose-700 border-rose-200",
  softgel: "bg-orange-100 text-orange-700 border-orange-200",
};

export const NPD_PRODUCT_TYPE_LABELS: Record<string, string> = {
  powder: "Powder",
  liquid: "Liquid",
  capsule: "Capsule",
  tablet: "Tablet",
  softgel: "Softgel",
};

// Priority colors for card left borders (hex values)
export const NPD_PRIORITY_BORDER_COLORS: Record<string, string> = {
  urgent: "#DC2626",
  high: "#F59E0B",
  normal: "#0BBBD5",
};

// ── Phase 3 Hazmat / Stability Testing ───────────────────────────────────────

export const STABILITY_TEST_STATUS_COLORS: Record<string, string> = {
  overdue:  "bg-destructive/20 text-destructive",
  due_soon: "bg-warning/20 text-warning-foreground",
  upcoming: "bg-primary/15 text-primary",
} as const;

export const STABILITY_TEST_STATUS_LABELS: Record<string, string> = {
  overdue:  "Overdue",
  due_soon: "Due Soon",
  upcoming: "Upcoming",
} as const;

export const STABILITY_TEST_RESULT_COLORS: Record<string, string> = {
  pass:         "bg-success/20 text-success",
  fail:         "bg-destructive/20 text-destructive",
  pending:      "bg-warning/20 text-warning-foreground",
  not_required: "bg-muted text-muted-foreground",
} as const;

export const STABILITY_TEST_RESULT_LABELS: Record<string, string> = {
  pass:         "Pass",
  fail:         "Fail",
  pending:      "Pending",
  not_required: "Not Required",
} as const;

// ── Phase 3 Slotting ──────────────────────────────────────────────────────────

export const PICK_VELOCITY_COLORS: Record<string, string> = {
  high:   "bg-success/20 text-success",
  medium: "bg-primary/15 text-primary",
  low:    "bg-muted text-muted-foreground",
} as const;

export const PICK_VELOCITY_LABELS: Record<string, string> = {
  high:   "High",
  medium: "Medium",
  low:    "Low",
} as const;

/** Returns a Tailwind badge class for cross-dock urgency based on days until MO start. */
export function getUrgencyBadgeClass(daysUntilStart: number): string {
  if (daysUntilStart <= 3) return "bg-destructive/20 text-destructive";
  if (daysUntilStart <= 7) return "bg-warning/20 text-warning-foreground";
  return "bg-primary/15 text-primary";
}

/** Badge classes for DG segregation severity (used in HazmatTab conflict table). */
export const DG_SEVERITY_COLORS: Record<string, string> = {
  HIGH:   "bg-destructive/20 text-destructive",
  MEDIUM: "bg-warning/20 text-warning-foreground",
  LOW:    "bg-muted text-muted-foreground",
} as const;

/** Single badge colour applied to any DG class identifier (always a hazard indicator). */
export const DG_CLASS_BADGE_COLOR = "bg-destructive/20 text-destructive";

// ── Helpers ──────────────────────────────────────────────────────────────────

export function getStatusColor(map: Record<string, string>, status: string): string {
  return map[status] ?? "bg-muted text-muted-foreground";
}

export function getStatusLabel(map: Record<string, string>, status: string): string {
  return map[status] ?? status;
}

// ── Warehouse Overview KPI card alert colours ─────────────────────────────────

export const KPI_ALERT_COLORS: Record<"amber" | "red" | "blue", string> = {
  amber: "text-warning-foreground",
  red:   "text-destructive",
  blue:  "text-info",
} as const;

// ── Sampling Inspection Status ────────────────────────────────────────────────

export const SAMPLING_STATUS_LABELS: Record<string, string> = {
  pending:     "Pending",
  in_progress: "In Progress",
  passed:      "Passed",
  failed:      "Failed",
  waived:      "Waived",
} as const;

export const SAMPLING_STATUS_COLORS: Record<string, string> = {
  pending:     "bg-muted text-muted-foreground",
  in_progress: "bg-warning/20 text-warning-foreground",
  passed:      "bg-success/20 text-success",
  failed:      "bg-destructive/20 text-destructive",
  waived:      "bg-primary/15 text-primary",
} as const;

// ── CoA Overall Status ────────────────────────────────────────────────────────

export const COA_STATUS_LABELS: Record<string, string> = {
  pass:          "Pass",
  minor_fail:    "Minor Fail",
  critical_fail: "Critical Fail",
  no_results:    "No Results",
  undetermined:  "Undetermined",
} as const;

export const COA_STATUS_COLORS: Record<string, string> = {
  pass:          "bg-success/20 text-success",
  minor_fail:    "bg-warning/20 text-warning-foreground",
  critical_fail: "bg-destructive/20 text-destructive",
  no_results:    "bg-muted text-muted-foreground",
  undetermined:  "bg-muted text-muted-foreground",
} as const;

// ── NCR Disposition Type ──────────────────────────────────────────────────────

export const DISPOSITION_TYPE_LABELS: Record<string, string> = {
  rework:              "Rework",
  scrap:               "Scrap",
  return_to_supplier:  "Return to Supplier",
  use_as_is:           "Use As-Is",
  regrade:             "Regrade",
  destroy:             "Destroy",
  pending_decision:    "Pending Decision",
} as const;

export const DISPOSITION_TYPE_COLORS: Record<string, string> = {
  rework:              "bg-warning/20 text-warning-foreground",
  scrap:               "bg-destructive/20 text-destructive",
  return_to_supplier:  "bg-primary/15 text-primary",
  use_as_is:           "bg-success/20 text-success",
  regrade:             "bg-secondary/20 text-secondary-foreground",
  destroy:             "bg-destructive/20 text-destructive",
  pending_decision:    "bg-muted text-muted-foreground",
} as const;

// ── Phase 2B — ASN / Landed Cost / RMA ───────────────────────────────────────

export const ASN_STATUS_LABELS: Record<string, string> = {
  pending:            "Pending",
  in_transit:         "In Transit",
  arrived:            "Arrived",
  partially_received: "Partially Received",
  received:           "Received",
  cancelled:          "Cancelled",
} as const;

export const ASN_STATUS_COLORS: Record<string, string> = {
  pending:            "bg-muted text-muted-foreground",
  in_transit:         "bg-blue-100 text-blue-800 border-blue-200",
  arrived:            "bg-amber-100 text-amber-800 border-amber-200",
  partially_received: "bg-orange-100 text-orange-800 border-orange-200",
  received:           "bg-green-100 text-green-800 border-green-200",
  cancelled:          "bg-muted text-muted-foreground",
} as const;

export const LANDED_COST_TYPE_LABELS: Record<string, string> = {
  freight:      "Freight",
  customs_duty: "Customs Duty",
  import_tax:   "Import Tax",
  insurance:    "Insurance",
  handling:     "Handling",
  other:        "Other",
} as const;

export const LANDED_COST_STATUS_LABELS: Record<string, string> = {
  pending:   "Pending",
  allocated: "Allocated",
  reversed:  "Reversed",
} as const;

export const LANDED_COST_STATUS_COLORS: Record<string, string> = {
  pending:   "bg-amber-100 text-amber-800 border-amber-200",
  allocated: "bg-green-100 text-green-800 border-green-200",
  reversed:  "bg-muted text-muted-foreground",
} as const;

export const RMA_STATUS_LABELS: Record<string, string> = {
  draft:                "Draft",
  approved:             "Approved",
  in_transit:           "In Transit",
  received_by_supplier: "Received by Supplier",
  credit_pending:       "Credit Pending",
  closed:               "Closed",
  cancelled:            "Cancelled",
} as const;

export const RMA_STATUS_COLORS: Record<string, string> = {
  draft:                "bg-muted text-muted-foreground",
  approved:             "bg-primary/15 text-primary",
  in_transit:           "bg-blue-100 text-blue-800 border-blue-200",
  received_by_supplier: "bg-amber-100 text-amber-800 border-amber-200",
  credit_pending:       "bg-orange-100 text-orange-800 border-orange-200",
  closed:               "bg-green-100 text-green-800 border-green-200",
  cancelled:            "bg-muted text-muted-foreground",
} as const;

export const RMA_RETURN_REASON_LABELS: Record<string, string> = {
  wrong_item:            "Wrong Item",
  damaged:               "Damaged",
  substandard_quality:   "Substandard Quality",
  excess_delivery:       "Excess Delivery",
  specification_failure: "Specification Failure",
  contamination:         "Contamination",
  near_expiry:           "Near Expiry",
  other:                 "Other",
} as const;

// ── Purchase Requisition ──────────────────────────────────────────────────────

export const PR_STATUS_COLORS: Record<string, string> = {
  draft:           "bg-muted text-muted-foreground",
  submitted:       "bg-info/20 text-info",
  approved:        "bg-success/20 text-success",
  rejected:        "bg-destructive/20 text-destructive",
  converted_to_po: "bg-primary/20 text-primary",
  cancelled:       "bg-muted text-muted-foreground",
} as const;

export const PR_PRIORITY_COLORS: Record<string, string> = {
  low:    "bg-muted text-muted-foreground",
  normal: "bg-info/20 text-info",
  high:   "bg-warning/20 text-warning-foreground",
  urgent: "bg-destructive/20 text-destructive",
} as const;

export const SUBSTITUTION_STATUS_COLORS: Record<string, string> = {
  approved: "bg-success/20 text-success",
  pending:  "bg-muted text-muted-foreground",
} as const;

// ── Replenishment Task ────────────────────────────────────────────────────────

export const REPLENISHMENT_TASK_STATUS_COLORS: Record<string, string> = {
  pending:     "bg-amber-100 text-amber-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed:   "bg-green-100 text-green-800",
  cancelled:   "bg-muted text-muted-foreground",
} as const;

export const REPLENISHMENT_TASK_STATUS_LABELS: Record<string, string> = {
  pending:     "Pending",
  in_progress: "In Progress",
  completed:   "Completed",
  cancelled:   "Cancelled",
} as const;

// ── Pick Wave ─────────────────────────────────────────────────────────────────

export const WAVE_STATUS_COLORS: Record<string, string> = {
  planned:     "bg-muted text-muted-foreground",
  released:    "bg-info/20 text-info",
  in_progress: "bg-warning/20 text-warning-foreground",
  completed:   "bg-success/20 text-success",
  cancelled:   "bg-destructive/20 text-destructive",
} as const;

export const WAVE_STATUS_LABELS: Record<string, string> = {
  planned:     "Planned",
  released:    "Released",
  in_progress: "In Progress",
  completed:   "Completed",
  cancelled:   "Cancelled",
} as const;

// ── Item Lifecycle ────────────────────────────────────────────────────────────

export const ITEM_LIFECYCLE_LABELS: Record<string, string> = {
  active:    "Active",
  phase_out: "Phase Out",
  obsolete:  "Obsolete",
} as const;

export const ITEM_LIFECYCLE_COLORS: Record<string, string> = {
  active:    "bg-success/20 text-success",
  phase_out: "bg-warning/20 text-warning-foreground",
  obsolete:  "bg-muted text-muted-foreground",
} as const;

// ── Stocktake Variance Investigation Status ───────────────────────────────────

export const INVESTIGATION_STATUS_LABELS: Record<string, string> = {
  none:              "None",
  pending_recount:   "Pending Recount",
  recounted:         "Recounted",
  pending_approval:  "Pending Approval",
  approved:          "Approved",
  rejected:          "Rejected",
} as const;

export const INVESTIGATION_STATUS_COLORS: Record<string, string> = {
  none:              "bg-muted text-muted-foreground",
  pending_recount:   "bg-warning/20 text-warning-foreground",
  recounted:         "bg-primary/15 text-primary",
  pending_approval:  "bg-warning/20 text-warning-foreground",
  approved:          "bg-success/20 text-success",
  rejected:          "bg-destructive/20 text-destructive",
} as const;

// ── Report Template Category Colours ─────────────────────────────────────────
// Mirrors the five report_category CHECK values defined in the Phase 2D migration.

export const REPORT_CATEGORY_COLORS: Record<string, string> = {
  warehouse:      "bg-blue-100 text-blue-800",
  quality:        "bg-green-100 text-green-800",
  manufacturing:  "bg-purple-100 text-purple-800",
  purchasing:     "bg-amber-100 text-amber-800",
  analytics:      "bg-indigo-100 text-indigo-800",
} as const;

// ══════════════════════════════════════════════════════════════════════════════
// JRB (Job Ready Bay) System Status Maps
// ══════════════════════════════════════════════════════════════════════════════

// ── JRB Bay Status ──────────────────────────────────────────────────────────

export const JRB_BAY_STATUS_LABELS: Record<string, string> = {
  available:    "Available",
  earmarked:    "Earmarked",
  occupied:     "Occupied",
  discrepancy:  "Discrepancy",
  collected:    "Collected",
  released:     "Released",
} as const;

export const JRB_BAY_STATUS_COLORS: Record<string, string> = {
  available:    "bg-muted text-muted-foreground",
  earmarked:    "bg-warning/15 text-warning",
  occupied:     "bg-success/15 text-success",
  discrepancy:  "bg-destructive/15 text-destructive",
  collected:    "bg-muted text-muted-foreground",
  released:     "bg-muted text-muted-foreground",
} as const;

// Bay grid card variant — includes border class for the 8×3 grid display
export const JRB_BAY_GRID_COLORS: Record<string, string> = {
  available:    "bg-muted/50 border-muted-foreground/20",
  earmarked:    "bg-yellow-500/15 border-yellow-500/40",
  occupied:     "bg-green-500/15 border-green-500/40",
  discrepancy:  "bg-destructive/15 border-destructive/40",
  collected:    "bg-muted border-muted-foreground/30",
  released:     "bg-muted/50 border-muted-foreground/20",
} as const;

// ── Staging Assignment Status ───────────────────────────────────────────────

export const STAGING_ASSIGNMENT_STATUS_LABELS: Record<string, string> = {
  earmarked:         "Earmarked",
  awaiting_transfer: "Awaiting Transfer",
  in_transit:        "In Transit",
  occupied:          "Occupied",
  verified:          "Verified",
  discrepancy:       "Discrepancy",
  collected:         "Collected",
  released:          "Released",
} as const;

export const STAGING_ASSIGNMENT_STATUS_COLORS: Record<string, string> = {
  earmarked:         "bg-warning/15 text-warning",
  awaiting_transfer: "bg-amber-100 text-amber-700",
  in_transit:        "bg-info/15 text-info",
  occupied:          "bg-success/15 text-success",
  verified:          "bg-success/15 text-success",
  discrepancy:       "bg-destructive/15 text-destructive",
  collected:         "bg-muted text-muted-foreground",
  released:          "bg-muted text-muted-foreground",
} as const;

// ── Staging Item Status ─────────────────────────────────────────────────────

export const STAGING_ITEM_STATUS_LABELS: Record<string, string> = {
  pending:          "Pending",
  staged:           "Staged",
  verified:         "Verified",
  consumed:         "Consumed",
  excess_returned:  "Excess Returned",
  discrepancy:      "Discrepancy",
} as const;

export const STAGING_ITEM_STATUS_COLORS: Record<string, string> = {
  pending:          "bg-muted text-muted-foreground",
  staged:           "bg-cyan-100 text-cyan-700",
  verified:         "bg-success/15 text-success",
  consumed:         "bg-muted text-muted-foreground",
  excess_returned:  "bg-muted text-muted-foreground",
  discrepancy:      "bg-destructive/15 text-destructive",
} as const;

// ── Staging Item Type ───────────────────────────────────────────────────────

export const STAGING_ITEM_TYPE_LABELS: Record<string, string> = {
  dispensed_item: "Dispensed",
  bulk_pack:      "Bulk Pack",
} as const;

export const STAGING_ITEM_TYPE_COLORS: Record<string, string> = {
  dispensed_item: "bg-purple-100 text-purple-700",
  bulk_pack:      "bg-muted text-muted-foreground",
} as const;

// ── MO Staging Status ───────────────────────────────────────────────────────

export const MO_STAGING_STATUS_LABELS: Record<string, string> = {
  not_staged:              "Not Staged",
  awaiting_materials:      "Awaiting Materials",
  partially_staged:        "Partially Staged",
  fully_staged:            "Fully Staged",
  ready_for_production:    "Ready for Production",
  in_production:           "In Production",
  staging_discrepancy:     "Staging Discrepancy",
  staging_released:        "Staging Released",
} as const;

export const MO_STAGING_STATUS_COLORS: Record<string, string> = {
  not_staged:              "bg-muted text-muted-foreground",
  awaiting_materials:      "bg-warning/15 text-warning",
  partially_staged:        "bg-amber-100 text-amber-700",
  fully_staged:            "bg-info/15 text-info",
  ready_for_production:    "bg-success/15 text-success",
  in_production:           "bg-primary/15 text-primary",
  staging_discrepancy:     "bg-destructive/15 text-destructive",
  staging_released:        "bg-muted text-muted-foreground",
} as const;

// ── Job Type ────────────────────────────────────────────────────────────────

export const JOB_TYPE_LABELS: Record<string, string> = {
  blending:   "Blending",
  production: "Production",
} as const;

export const JOB_TYPE_COLORS: Record<string, string> = {
  blending:   "bg-cyan-100 text-cyan-700",
  production: "bg-blue-900/15 text-blue-900",
} as const;

// ── Precautionary allergen (PAL) sign-off ──────────────────────────────────
// Mirrored in ios/Operations App/Utilities/StatusMaps.swift.

export type PalSignoffState = "not_ready" | "unassessed" | "stale" | "current";

export const PAL_SIGNOFF_STATE_LABELS: Record<PalSignoffState, string> = {
  // Derived since 20422000000000: only not_ready and current are produced. The other
  // two are kept so an old cached response still renders.
  not_ready: "Can't confirm yet",
  unassessed: "Not confirmed",
  stale: "Not confirmed",
  current: "Confirmed",
};

export const PAL_SIGNOFF_STATE_BADGE_VARIANTS: Record<PalSignoffState, "destructive" | "outline" | "secondary"> = {
  not_ready: "secondary",
  unassessed: "destructive",
  stale: "destructive",
  current: "outline",
};

// ── Pre-Staging Bay Status ──────────────────────────────────────────────────

export const PRESTAGING_BAY_STATUS_LABELS: Record<string, string> = {
  available:  "Available",
  in_use:     "In Use",
  completed:  "Completed",
} as const;

export const PRESTAGING_BAY_STATUS_COLORS: Record<string, string> = {
  available:  "bg-muted text-muted-foreground",
  in_use:     "bg-success/15 text-success",
  completed:  "bg-muted text-muted-foreground",
} as const;

// ── Discrepancy Resolution Status ───────────────────────────────────────────

export const DISCREPANCY_RESOLUTION_LABELS: Record<string, string> = {
  open:                      "Open",
  replacement_in_progress:   "Replacement In Progress",
  resolved:                  "Resolved",
  escalated:                 "Escalated",
} as const;

export const DISCREPANCY_RESOLUTION_COLORS: Record<string, string> = {
  open:                      "bg-destructive/15 text-destructive",
  replacement_in_progress:   "bg-warning/15 text-warning",
  resolved:                  "bg-success/15 text-success",
  escalated:                 "bg-orange-100 text-orange-700",
} as const;

// ── Supplier Status & Risk ──────────────────────────────────────────────────

export const SUPPLIER_STATUS_LABELS: Record<string, string> = {
  approved:        "Approved",
  provisional:     "Provisional",
  not_approved:    "Not Approved",
  suspended:       "Suspended",
  pending_review:  "Pending Review",
} as const;

export const SUPPLIER_STATUS_COLORS: Record<string, string> = {
  approved:        "bg-green-100 text-green-700",
  provisional:     "bg-yellow-100 text-yellow-700",
  not_approved:    "bg-destructive/20 text-destructive",
  suspended:       "bg-destructive/20 text-destructive",
  pending_review:  "bg-muted text-muted-foreground",
} as const;

export const SUPPLIER_RISK_LABELS: Record<string, string> = {
  low:      "Low Risk",
  medium:   "Medium Risk",
  high:     "High Risk",
  critical: "Critical Risk",
} as const;

export const SUPPLIER_RISK_COLORS: Record<string, string> = {
  low:      "border-green-300 bg-green-50 text-green-700",
  medium:   "border-yellow-300 bg-yellow-50 text-yellow-700",
  high:     "border-red-300 bg-red-50 text-red-700",
  critical: "border-red-500 bg-red-100 text-red-800",
} as const;

// ── Error Logging System ────────────────────────────────────────────────────

export const ERROR_LOG_STATUS_COLORS: Record<string, string> = {
  new:           "bg-destructive/20 text-destructive",
  acknowledged:  "bg-warning/20 text-warning-foreground",
  investigating: "bg-primary/20 text-primary",
  resolved:      "bg-success/20 text-success",
  dismissed:     "bg-muted text-muted-foreground",
  duplicate:     "bg-muted text-muted-foreground",
} as const;

export const ERROR_LOG_STATUS_LABELS: Record<string, string> = {
  new:           "New",
  acknowledged:  "Acknowledged",
  investigating: "Investigating",
  resolved:      "Resolved",
  dismissed:     "Dismissed",
  duplicate:     "Duplicate",
} as const;

export const ERROR_SEVERITY_COLORS: Record<string, string> = {
  critical: "bg-destructive/20 text-destructive",
  error:    "bg-orange-100 text-orange-800",
  warning:  "bg-warning/20 text-warning-foreground",
  info:     "bg-primary/15 text-primary",
} as const;

export const ERROR_SEVERITY_LABELS: Record<string, string> = {
  critical: "Critical",
  error:    "Error",
  warning:  "Warning",
  info:     "Info",
} as const;

export const ERROR_TYPE_LABELS: Record<string, string> = {
  render:          "Render",
  network:         "Network",
  supabase_query:  "Supabase Query",
  supabase_rpc:    "Supabase RPC",
  edge_function:   "Edge Function",
  auth:            "Auth",
  validation:      "Validation",
  navigation:      "Navigation",
  unhandled:       "Unhandled",
  ios_service:     "iOS Service",
  ios_crash:       "iOS Crash",
  user_report:     "User Report",
  unknown:         "Unknown",
} as const;

export const ERROR_PLATFORM_LABELS: Record<string, string> = {
  web:           "Web",
  ios:           "iOS",
  edge_function: "Edge Function",
} as const;

// ── Customer Order Book Report (F-01) ────────────────────────────────────────

/** Internal monitoring beacon — staff view only, never shown to customers */
export const INTERNAL_BEACON_COLORS: Record<string, string> = {
  on_track:        "bg-green-100 text-green-800",
  monitor:         "bg-amber-100 text-amber-800",
  needs_attention:  "bg-red-100 text-red-800",
  no_mo:           "bg-gray-100 text-gray-500",
} as const;

export const INTERNAL_BEACON_LABELS: Record<string, string> = {
  on_track:        "On Track",
  monitor:         "Monitor",
  needs_attention: "Needs Attention",
  no_mo:           "No MO Linked",
} as const;

export const INTERNAL_BEACON_ICONS: Record<string, string> = {
  on_track:        "●",
  monitor:         "●",
  needs_attention: "●",
  no_mo:           "○",
} as const;

/** Customer-facing status — staff-set, the only status shown on customer output */
export const CUSTOMER_FACING_STATUS_COLORS: Record<string, string> = {
  on_schedule:             "bg-green-100 text-green-800",
  in_production:           "bg-blue-100 text-blue-800",
  scheduling_in_progress:  "bg-amber-100 text-amber-800",
  updated_eta:             "bg-orange-100 text-orange-800",
  ready_for_dispatch:      "bg-indigo-100 text-indigo-800",
  dispatched:              "bg-emerald-100 text-emerald-800",
} as const;

export const CUSTOMER_FACING_STATUS_LABELS: Record<string, string> = {
  on_schedule:            "On Schedule",
  in_production:          "In Production",
  scheduling_in_progress: "Scheduling in Progress",
  updated_eta:            "Updated ETA — See Note",
  ready_for_dispatch:     "Ready for Dispatch",
  dispatched:             "Dispatched",
} as const;

/** Schedule confirmation status */
export const SCHEDULE_STATUS_LABELS: Record<string, string> = {
  confirmed:   "Confirmed",
  provisional: "Provisional",
} as const;

export const SCHEDULE_STATUS_COLORS: Record<string, string> = {
  confirmed:   "bg-green-100 text-green-800",
  provisional: "bg-amber-100 text-amber-700",
} as const;

// ── Order Book Delivery Status (replaces two-layer beacon model) ──────────────

export const ORDER_BOOK_DELIVERY_STATUS_COLORS: Record<string, string> = {
  on_time:                 "bg-green-100 text-green-800 border-green-300",
  early_delivery_possible: "bg-blue-100 text-blue-800 border-blue-300",
  delayed:                 "bg-red-100 text-red-800 border-red-300",
  in_progress:             "bg-slate-100 text-slate-700 border-slate-300",
  // 'unscheduled' is an allowed delivery_status (CHECK since 20260422100000) and the
  // most common value — style it neutrally so it doesn't render as raw lowercase text.
  unscheduled:             "bg-slate-50 text-slate-600 border-slate-200",
} as const;

export const ORDER_BOOK_DELIVERY_STATUS_LABELS: Record<string, string> = {
  on_time:                 "On-time",
  early_delivery_possible: "Early Delivery Possible",
  delayed:                 "Delayed",
  in_progress:             "In Progress",
  unscheduled:             "Unscheduled",
} as const;

// ── Order Book Lifecycle (PROGRESS axis — derived, never stored) ──────────────
//
// Distinct from ORDER_BOOK_DELIVERY_STATUS_* above, which is the SCHEDULE axis and is
// staff-set. Lifecycle is derived at read time from manufacturing_orders.status +
// shipped_quantity (see src/lib/orderBook/orderBookLifecycle.ts) and answers "how far
// has this line got?" rather than "is it on time?". The two are shown side by side.

export const ORDER_BOOK_LIFECYCLE_COLORS: Record<string, string> = {
  unlinked:      "bg-slate-50 text-slate-600 border-slate-200",
  in_production: "bg-blue-100 text-blue-800 border-blue-300",
  // Matches MO_STATUS_COLORS.completed's success tone — the same fact, same colour.
  completed:     "bg-success/15 text-success border-success/30",
  // Deliberately NOT the same tone as completed: dispatched and merely-produced are
  // different states and the owner asked for "a more detailed view", so they must be
  // distinguishable at a glance rather than two shades of done.
  shipped:       "bg-violet-100 text-violet-800 border-violet-300",
  cancelled:     "bg-red-100 text-red-800 border-red-300",
} as const;

export const ORDER_BOOK_LIFECYCLE_LABELS: Record<string, string> = {
  unlinked:      "Not scheduled",
  in_production: "In Production",
  completed:     "Completed",
  shipped:       "Shipped",
  cancelled:     "Cancelled",
} as const;

// ── MRPeasy item-gap register (Planning → Data Flow) ──────────────────────────
//
// Severity is DERIVED IN SQL by v_mrpeasy_item_gaps; these maps only render it.

export const ITEM_GAP_SEVERITY_COLORS: Record<string, string> = {
  // Live stock for an item QF cannot name — the only operationally urgent band.
  live_inventory: "bg-red-100 text-red-800 border-red-300",
  stuck:          "bg-amber-100 text-amber-800 border-amber-300",
  // Ordinary sync lag (0-1 days measured). Neutral on purpose: this fires for every
  // newly added ingredient and must not read as a problem.
  awaiting_sync:  "bg-slate-100 text-slate-700 border-slate-300",
  history_only:   "bg-slate-50 text-slate-600 border-slate-200",
} as const;

export const ITEM_GAP_SEVERITY_LABELS: Record<string, string> = {
  live_inventory: "Live stock, no item",
  stuck:          "Stuck",
  awaiting_sync:  "Awaiting sync",
  history_only:   "History only",
} as const;

export const ITEM_GAP_KIND_LABELS: Record<string, string> = {
  mrpeasy_article_unknown_to_qf: "In MRPeasy, not in QF",
  ingredient_awaiting_mrpeasy:   "In QF, awaiting MRPeasy",
} as const;

/** Tone → badge classes for the Order Book "due" relative-date badge. */
export const ORDER_BOOK_DUE_TONE_COLORS: Record<string, string> = {
  overdue: "bg-warning/15 text-warning border-warning/30",
  soon:    "bg-muted text-muted-foreground",
  today:   "bg-primary/15 text-primary border-primary/30",
  none:    "bg-muted text-muted-foreground",
} as const;

// ── Dashboard ─────────────────────────────────────────────────────────────────

/** Numeric sub-phase stage labels used by the NPD dashboard widget (legacy integer stage field) */
export const NPD_STAGE_NUM_LABELS: Record<number, string> = {
  1: "Scoping",
  2: "Development",
  3: "QA Review",
  4: "Prod Setup",
} as const;

/** Numeric sub-phase stage hex colors used by the NPD dashboard widget */
export const NPD_STAGE_NUM_COLORS: Record<number, string> = {
  1: "#F59E0B",
  2: "#3B82F6",
  3: "#8B5CF6",
  4: "#10B981",
} as const;

/** Task module badge display — label + Tailwind bg/text classes */
export const TASK_MODULE_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  npd:           { label: "NPD",           bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]" },
  quality:       { label: "Quality",       bg: "bg-[#10B981]/10", text: "text-[#10B981]" },
  manufacturing: { label: "Manufacturing", bg: "bg-[#6366F1]/10", text: "text-[#6366F1]" },
  purchasing:    { label: "Purchasing",    bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" },
  general:       { label: "General",       bg: "bg-[#64748B]/10", text: "text-[#64748B]" },
} as const;

/** Priority dot colour class — used in task feed row indicators */
export const TASK_PRIORITY_DOT: Record<string, string> = {
  urgent: "bg-[#EF4444]",
  high:   "bg-[#F59E0B]",
  normal: "bg-[#0BBBD5]",
  low:    "bg-[#94A3B8]",
} as const;

/** Activity feed icon + colour config per activity source (mo / gr / mv) */
export const ACTIVITY_ICON_CONFIG: Record<string, { icon: ElementType; bg: string; color: string }> = {
  mo: { icon: CheckCircle,    bg: "bg-[#10B981]/10", color: "text-[#10B981]" },
  gr: { icon: Package,        bg: "bg-[#0BBBD5]/10", color: "text-[#0BBBD5]" },
  mv: { icon: ArrowRightLeft, bg: "bg-[#3B82F6]/10", color: "text-[#3B82F6]" },
} as const;

// ── Payment Portal — Invoice Status ─────────────────────────────────────────

export const PORTAL_INVOICE_STATUS_COLORS: Record<string, string> = {
  unpaid:   "bg-red-100 text-red-700",
  partial:  "bg-amber-100 text-amber-700",
  paid:     "bg-green-100 text-green-700",
  overpaid: "bg-blue-100 text-blue-700",
  void:     "bg-muted text-muted-foreground",
} as const;

export const PORTAL_INVOICE_STATUS_LABELS: Record<string, string> = {
  unpaid:   "Unpaid",
  partial:  "Partially Paid",
  paid:     "Paid",
  overpaid: "Overpaid",
  void:     "Void",
} as const;

// ── Payment Portal — Session Status ─────────────────────────────────────────

export const PAYMENT_SESSION_STATUS_COLORS: Record<string, string> = {
  pending:    "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  completed:  "bg-green-100 text-green-700",
  failed:     "bg-red-100 text-red-700",
  expired:    "bg-muted text-muted-foreground",
  revoked:    "bg-muted text-muted-foreground",
} as const;

export const PAYMENT_SESSION_STATUS_LABELS: Record<string, string> = {
  pending:    "Link Active",
  processing: "Processing",
  completed:  "Payment Complete",
  failed:     "Failed",
  expired:    "Expired",
  revoked:    "Revoked",
} as const;

// ── Payment Portal — Transaction Status ─────────────────────────────────────

export const PAYMENT_TRANSACTION_STATUS_COLORS: Record<string, string> = {
  succeeded: "bg-green-100 text-green-700",
  failed:    "bg-red-100 text-red-700",
  refunded:  "bg-amber-100 text-amber-700",
  disputed:  "bg-orange-100 text-orange-700",
} as const;

export const PAYMENT_TRANSACTION_STATUS_LABELS: Record<string, string> = {
  succeeded: "Succeeded",
  failed:    "Failed",
  refunded:  "Refunded",
  disputed:  "Disputed",
} as const;

// ── Payment Portal — Webhook Event Status ───────────────────────────────────

export const WEBHOOK_EVENT_STATUS_COLORS: Record<string, string> = {
  received:    "bg-gray-100 text-gray-700",
  processing:  "bg-blue-100 text-blue-700",
  processed:   "bg-green-100 text-green-700",
  failed:      "bg-red-100 text-red-700",
  dead_letter: "bg-orange-100 text-orange-700",
} as const;

export const WEBHOOK_EVENT_STATUS_LABELS: Record<string, string> = {
  received:    "Received",
  processing:  "Processing",
  processed:   "Processed",
  failed:      "Failed",
  dead_letter: "Dead Letter",
} as const;

// ── Xero Sync — Queue Status ────────────────────────────────────────────────

export const XERO_SYNC_STATUS_COLORS: Record<string, string> = {
  queued:     "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  synced:     "bg-green-100 text-green-700",
  failed:     "bg-red-100 text-red-700",
  skipped:    "bg-muted text-muted-foreground",
} as const;

export const XERO_SYNC_STATUS_LABELS: Record<string, string> = {
  queued:     "Queued",
  processing: "Processing",
  synced:     "Synced",
  failed:     "Failed",
  skipped:    "Skipped",
} as const;

export const XERO_CONNECTION_STATUS_COLORS: Record<string, string> = {
  never:   "bg-muted text-muted-foreground",
  running: "bg-blue-100 text-blue-700",
  success: "bg-green-100 text-green-700",
  error:   "bg-red-100 text-red-700",
} as const;

export const XERO_CONNECTION_STATUS_LABELS: Record<string, string> = {
  never:   "Never Synced",
  running: "Syncing...",
  success: "Connected",
  error:   "Error",
} as const;

// ── Equipment ─────────────────────────────────────────────────────────────

export const EQUIPMENT_STATUS_COLORS: Record<string, string> = {
  pending_validation: "bg-amber-100 text-amber-700",
  active:             "bg-success/15 text-success",
  operational:        "bg-green-100 text-green-700",
  under_maintenance:  "bg-warning/15 text-warning",
  out_of_service:     "bg-destructive/15 text-destructive",
  decommissioned:     "bg-muted text-muted-foreground",
} as const;

export const EQUIPMENT_STATUS_LABELS: Record<string, string> = {
  pending_validation: "Pending Validation",
  active:             "Active",
  operational:        "Operational",
  under_maintenance:  "Under Maintenance",
  out_of_service:     "Out of Service",
  decommissioned:     "Decommissioned",
} as const;

export const EQUIPMENT_VALIDATION_STATUS_COLORS: Record<string, string> = {
  not_required: "bg-muted text-muted-foreground",
  required:     "bg-amber-100 text-amber-700",
  in_progress:  "bg-primary/15 text-primary",
  completed:    "bg-success/15 text-success",
  expired:      "bg-destructive/15 text-destructive",
} as const;

export const EQUIPMENT_VALIDATION_STATUS_LABELS: Record<string, string> = {
  not_required: "Not Required",
  required:     "Required",
  in_progress:  "In Progress",
  completed:    "Completed",
  expired:      "Expired",
} as const;

// ── AIC (APEX Intelligence Consultant) Status Maps ──────────────────────────

export const AIC_WARNING_SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: "bg-red-600 text-white",
  HIGH: "bg-orange-500 text-white",
  MEDIUM: "bg-amber-500 text-white",
  ADVISORY: "bg-blue-500 text-white",
};

export const AIC_WARNING_SEVERITY_LABELS: Record<string, string> = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  ADVISORY: "Advisory",
};

export const AIC_WARNING_CATEGORY_COLORS: Record<string, string> = {
  SR: "bg-red-100 text-red-800 border-red-200",
  CR: "bg-orange-100 text-orange-800 border-orange-200",
  SCR: "bg-yellow-100 text-yellow-800 border-yellow-200",
  QR: "bg-purple-100 text-purple-800 border-purple-200",
  AR: "bg-pink-100 text-pink-800 border-pink-200",
  DR: "bg-indigo-100 text-indigo-800 border-indigo-200",
  SO: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PO: "bg-teal-100 text-teal-800 border-teal-200",
  IO: "bg-cyan-100 text-cyan-800 border-cyan-200",
};

export const AIC_WARNING_CATEGORY_LABELS: Record<string, string> = {
  SR: "Supply Risk",
  CR: "Capacity Risk",
  SCR: "Schedule Risk",
  QR: "Quality Risk",
  AR: "Allergen Risk",
  DR: "Demand Risk",
  SO: "Sequencing Opportunity",
  PO: "Procurement Opportunity",
  IO: "Inventory Opportunity",
};

export const AIC_SUGGESTION_STATUS_COLORS: Record<string, string> = {
  pending: "bg-blue-100 text-blue-800",
  accepted: "bg-green-100 text-green-800",
  dismissed: "bg-gray-100 text-gray-800",
  actioned: "bg-emerald-100 text-emerald-800",
  expired: "bg-gray-100 text-gray-500",
};

export const AIC_SUGGESTION_STATUS_LABELS: Record<string, string> = {
  pending: "Pending Review",
  accepted: "Accepted",
  dismissed: "Dismissed",
  actioned: "Actioned",
  expired: "Expired",
};

export const AIC_DOMAIN_LABELS: Record<string, string> = {
  failure_modes: "Failure Modes",
  heuristics: "Heuristics",
  industry: "Industry Intelligence",
  operational: "Operational Patterns",
  efficiency: "Efficiency",
};

export const AIC_AGENT_LABELS: Record<number, string> = {
  1: "Failure Mode Analyst",
  2: "Planning Excellence Researcher",
  3: "Industry & Regulatory Intelligence",
  4: "Operational Pattern Miner",
  5: "Efficiency Opportunity Scout",
};

// ── Quality Inbox ────────────────────────────────────────────────

export const QUALITY_INBOX_STATUS_COLORS: Record<string, string> = {
  received: "bg-gray-100 text-gray-700",
  processing: "bg-blue-100 text-blue-700",
  classified: "bg-indigo-100 text-indigo-700",
  matched: "bg-purple-100 text-purple-700",
  auto_attached: "bg-green-100 text-green-700",
  review_pending: "bg-amber-100 text-amber-700",
  manual_queue: "bg-orange-100 text-orange-700",
  completed: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  dead_letter: "bg-red-200 text-red-800",
};

export const QUALITY_INBOX_STATUS_LABELS: Record<string, string> = {
  received: "Received",
  processing: "Processing",
  classified: "Classified",
  matched: "Matched",
  auto_attached: "Auto-Attached",
  review_pending: "Pending Review",
  manual_queue: "Manual Queue",
  completed: "Completed",
  failed: "Failed",
  dead_letter: "Dead Letter",
};

export const QUALITY_INBOX_DOC_TYPE_COLORS: Record<string, string> = {
  COA: "bg-teal-100 text-teal-700",
  SDS: "bg-sky-100 text-sky-700",
  SPEC: "bg-violet-100 text-violet-700",
  OTHER: "bg-gray-100 text-gray-600",
};

export const QUALITY_INBOX_DOC_TYPE_LABELS: Record<string, string> = {
  COA: "Certificate of Analysis",
  SDS: "Safety Data Sheet",
  SPEC: "Specification",
  OTHER: "Other",
};

// ── Products ────────────────────────────────────────────────────────────────

export const PRODUCT_STATUS_STYLES: Record<string, string> = {
  in_development: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200",
  discontinued: "bg-red-50 text-red-700 border-red-200",
  archived: "bg-gray-100 text-gray-500 border-gray-200",
};

export const PRODUCT_STATUS_LABELS: Record<string, string> = {
  in_development: "In Development",
  active: "Active",
  on_hold: "On Hold",
  discontinued: "Discontinued",
  archived: "Archived",
};

export const PRODUCT_TYPE_STYLES: Record<string, string> = {
  powder: "bg-orange-50 text-orange-700 border-orange-200",
  capsule: "bg-purple-50 text-purple-700 border-purple-200",
  tablet: "bg-cyan-50 text-cyan-700 border-cyan-200",
  liquid: "bg-sky-50 text-sky-700 border-sky-200",
  gummy: "bg-pink-50 text-pink-700 border-pink-200",
};

export const PRODUCT_JOB_STATUS_STYLES: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  confirmed: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

export const PRODUCT_NCR_SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-50 text-red-700 border-red-200",
  major: "bg-amber-50 text-amber-700 border-amber-200",
  minor: "bg-blue-50 text-blue-700 border-blue-200",
};

export const PRODUCT_FORMULATION_STAGE_STYLES: Record<string, string> = {
  scoping: "bg-gray-100 text-gray-600",
  preliminary_quote: "bg-blue-50 text-blue-700",
  client_decision: "bg-amber-50 text-amber-700",
  formulation: "bg-purple-50 text-purple-700",
  production_setup: "bg-teal-50 text-teal-700",
  released: "bg-emerald-50 text-emerald-700",
  superseded: "bg-gray-100 text-gray-400",
};

// ── Mock Recall ───────────────────────────────────────────────────────────────

export const MOCK_RECALL_STATUS_COLORS: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-amber-100 text-amber-800",
  signed_off: "bg-violet-100 text-violet-800",
  cancelled: "bg-gray-100 text-gray-500",
};

export const MOCK_RECALL_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  in_progress: "In Progress",
  completed: "Completed",
  signed_off: "Signed Off",
  cancelled: "Cancelled",
};

export const MOCK_RECALL_RESULT_COLORS: Record<string, string> = {
  pass: "bg-green-100 text-green-800",
  fail: "bg-red-100 text-red-800",
  conditional_pass: "bg-yellow-100 text-yellow-800",
};

export const MOCK_RECALL_RESULT_LABELS: Record<string, string> = {
  pass: "Pass",
  fail: "Fail",
  conditional_pass: "Conditional",
};

export const MOCK_RECALL_SCENARIO_LABELS: Record<string, string> = {
  allergen_mislabel: "Allergen Mislabel",
  pathogen_contamination: "Pathogen",
  foreign_body: "Foreign Body",
  chemical_contamination: "Chemical",
  label_error: "Label Error",
  oos_ingredient: "OOS Ingredient",
  packaging_defect: "Packaging Defect",
  complaint_escalation: "Complaint Escalation",
  custom: "Custom",
};

export const MOCK_RECALL_SCENARIO_COLORS: Record<string, string> = {
  allergen_mislabel: "bg-amber-100 text-amber-800",
  pathogen_contamination: "bg-pink-100 text-pink-800",
  foreign_body: "bg-red-100 text-red-800",
  chemical_contamination: "bg-orange-100 text-orange-800",
  label_error: "bg-indigo-100 text-indigo-800",
  oos_ingredient: "bg-purple-100 text-purple-800",
  packaging_defect: "bg-cyan-100 text-cyan-800",
  complaint_escalation: "bg-rose-100 text-rose-800",
  custom: "bg-gray-100 text-gray-700",
};

export const MOCK_RECALL_GAP_SEVERITY_COLORS: Record<string, string> = {
  critical: "bg-red-100 text-red-800",
  major: "bg-orange-100 text-orange-800",
  minor: "bg-yellow-100 text-yellow-800",
  observation: "bg-blue-100 text-blue-800",
};

export const MOCK_RECALL_GAP_CATEGORY_LABELS: Record<string, string> = {
  documentation: "Documentation",
  training: "Training",
  system: "System",
  process: "Process",
  data_quality: "Data Quality",
  communication: "Communication",
  traceability: "Traceability",
  timeliness: "Timeliness",
};

export const MOCK_RECALL_SCOPE_LABELS: Record<string, string> = {
  trade_level: "Trade Level",
  consumer_level: "Consumer Level",
};

export const MOCK_RECALL_FRAMEWORK_LABELS: Record<string, string> = {
  fsanz: "FSANZ",
  sqf: "SQF",
  brcgs: "BRCGS",
  fssc_22000: "FSSC 22000",
};

// ============================================================
// Command Centre Status Maps
// ============================================================

export const OVERRIDE_STATUS_MAP: Record<string, { label: string; color: string; icon?: string }> = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: "Clock" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800", icon: "CheckCircle" },
  denied: { label: "Denied", color: "bg-red-100 text-red-800", icon: "XCircle" },
};

export const OVERRIDE_TYPE_MAP: Record<string, { label: string }> = {
  equipment_swap: { label: "Equipment Swap" },
  cleaning_deviation: { label: "Cleaning Deviation" },
  competency_override: { label: "Competency Override" },
  parameter_adjustment: { label: "Parameter Adjustment" },
  skip_step: { label: "Skip Step" },
  material_substitution: { label: "Material Substitution" },
  other: { label: "Other" },
};

export const COMPETENCY_STATUS_MAP: Record<string, { label: string; color: string }> = {
  active: { label: "Active", color: "bg-green-100 text-green-800" },
  expired: { label: "Expired", color: "bg-red-100 text-red-800" },
  revoked: { label: "Revoked", color: "bg-gray-100 text-gray-800" },
  pending_assessment: { label: "Pending Assessment", color: "bg-yellow-100 text-yellow-800" },
};

export const COMPETENCY_LEVEL_MAP: Record<string, { label: string }> = {
  stage: { label: "Stage" },
  operation: { label: "Operation" },
  equipment: { label: "Equipment" },
};

export const CREW_ROLE_MAP: Record<string, { label: string; color: string }> = {
  line_leader: { label: "Line Leader", color: "bg-blue-100 text-blue-800" },
  support: { label: "Support", color: "bg-gray-100 text-gray-800" },
};

export const WASTAGE_CATEGORY_MAP: Record<string, { label: string }> = {
  setup_waste: { label: "Setup Waste" },
  faulty_defective: { label: "Faulty / Defective" },
  production_waste: { label: "Production Waste" },
  spillage_damage: { label: "Spillage / Damage" },
};

export const LINE_CONFIG_SOURCE_MAP: Record<string, { label: string; color: string }> = {
  default: { label: "Default", color: "bg-gray-100 text-gray-800" },
  added: { label: "Added", color: "bg-blue-100 text-blue-800" },
  swapped: { label: "Swapped", color: "bg-yellow-100 text-yellow-800" },
};

// ── Customer Label Proof Portal ───────────────────────────────────────────────

export const LABEL_PROOF_PORTAL_STATUS_STYLES: Record<string, string> = {
  awaiting_submission: "bg-warning/15 text-warning border-warning/30",
  in_review: "bg-info/15 text-info border-info/30",
  awaiting_brand_signoff: "bg-success/15 text-success border-success/30",
  on_hold: "bg-warning/15 text-warning border-warning/30",
  released: "bg-muted text-muted-foreground border-border",
};

export const LABEL_PROOF_PORTAL_STATUS_LABELS: Record<string, string> = {
  awaiting_submission: "Awaiting Submission",
  in_review: "In Review",
  awaiting_brand_signoff: "Awaiting Brand Sign-Off",
  on_hold: "On Hold",
  released: "Released",
};

export const LABEL_PROOF_PORTAL_STATUS_HELP: Record<string, string> = {
  awaiting_submission: "Waiting for artwork to be submitted.",
  in_review: "Superior Formulations is reviewing this label for regulatory compliance.",
  awaiting_brand_signoff: "Regulatory approval complete - please review and submit your decision.",
  on_hold: "This review is currently on hold. Superior Formulations will notify you when it resumes.",
  released: "This label has been approved and released.",
};

// Mirrors public.label_proof_project_is_open (migration 20366000000000). pgTAP 145
// pins the SQL side to the CHECK vocabulary; keep this list identical. `released`
// is the only closed status.
export const LABEL_PROOF_OPEN_STATUSES = [
  "draft",
  "awaiting_submission",
  "in_review",
  "awaiting_brand_signoff",
  "on_hold",
] as const;
export const isLabelProofProjectOpen = (status: string): boolean =>
  (LABEL_PROOF_OPEN_STATUSES as readonly string[]).includes(status);

export const LABEL_PROOF_REVIEW_DECISION_STYLES: Record<string, string> = {
  reviewing: "bg-muted text-muted-foreground border-border",
  approved: "bg-success/15 text-success border-success/30",
  approved_with_comments: "bg-info/15 text-info border-info/30",
  changes_requested: "bg-destructive/15 text-destructive border-destructive/30",
  withdrawn: "bg-muted text-muted-foreground border-border",
};

export const LABEL_PROOF_REVIEW_DECISION_LABELS: Record<string, string> = {
  reviewing: "Reviewing",
  approved: "Approved",
  approved_with_comments: "Approved with Comments",
  changes_requested: "Changes Requested",
  withdrawn: "Withdrawn",
};

// Canvas link access states — public.canvas_token_state (migration 20366000000100).
// Rendered on the staff Reviewer access panel; the state is DERIVED server-side
// (canvas_reviewer_states) and never re-computed from role/status on the client.
export const CANVAS_ACCESS_STATE_LABELS: Record<string, string> = {
  active: "Active",
  idle_expired: "Idle",
  on_hold: "On hold",
  read_only: "Read-only",
  closed: "Closed",
  revoked: "Revoked",
  superseded: "Replaced",
  invalid: "No link",
};

export const CANVAS_ACCESS_STATE_STYLES: Record<string, string> = {
  active: "bg-success/15 text-success border-success/30",
  idle_expired: "bg-warning/15 text-warning border-warning/30",
  on_hold: "bg-warning/15 text-warning border-warning/30",
  read_only: "bg-muted text-muted-foreground border-border",
  closed: "bg-muted text-muted-foreground border-border",
  revoked: "bg-destructive/15 text-destructive border-destructive/30",
  superseded: "bg-muted text-muted-foreground border-border",
  invalid: "bg-destructive/15 text-destructive border-destructive/30",
};

export const PRODUCT_PRICE_REQUEST_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

export const PRODUCT_PRICE_REQUEST_STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-muted text-muted-foreground border-border",
};

// ── MO Lifecycle (production cycle: planned → scheduled → staged → in_progress → complete) ─
// "Complete" here marks the end of production timing/labour booking — see
// LOT_LIFECYCLE_* below for the saleability state machine that runs separately.

export const MO_LIFECYCLE_LABELS: Record<string, string> = {
  planned: "Planned",
  pending_planning: "Awaiting Planning",
  scheduled: "Scheduled",
  staged: "Components Staged",
  in_progress: "In Progress",
  complete: "Production Complete",
  cancelled: "Cancelled",
};

export const MO_LIFECYCLE_COLORS: Record<string, string> = {
  planned: "bg-slate-100 text-slate-700 border-slate-200",
  pending_planning: "bg-amber-50 text-amber-700 border-amber-200",
  scheduled: "bg-sky-50 text-sky-700 border-sky-200",
  staged: "bg-indigo-50 text-indigo-700 border-indigo-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  complete: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

// ── Stock Lot lifecycle (the *output* of an MO — separate authority from production) ──
// Lot is born at `produced` when MO completes. `on_hold` is the QA window. `released`
// requires QA sign-off and unlocks the lot for shipping/consumption.

export const LOT_LIFECYCLE_LABELS: Record<string, string> = {
  planned: "Planned",
  produced: "Produced",
  on_hold: "Quality Hold",
  released: "Released",
  consumed: "Consumed",
  rejected: "Rejected",
};

export const LOT_LIFECYCLE_COLORS: Record<string, string> = {
  planned: "bg-slate-100 text-slate-700 border-slate-200",
  produced: "bg-blue-50 text-blue-700 border-blue-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200",
  released: "bg-emerald-50 text-emerald-700 border-emerald-200",
  consumed: "bg-muted text-muted-foreground border-border",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

// ── MO stage → customer-facing label translation ──────────────────────────────
// Internal stage codes (FOR/FIL/FCP) leak into customer conversations otherwise.
// CSRs use these labels when relaying progress; planners still see the codes.

export const MO_STAGE_CUSTOMER_LABELS: Record<string, string> = {
  for: "Blending",
  fil: "Filling",
  fcp: "Packing",
  // Non-manufactured fulfillment paths use these too:
  purchase: "Procurement",
  ship_from_stock: "Ready",
};

// ── CO line on-time indicator (revised_eta vs customer_expected_date) ────────

export const CO_LINE_ON_TIME_LABELS: Record<string, string> = {
  on_time: "On Time",
  at_risk: "At Risk",
  late: "Late",
  unknown: "—",
};

export const CO_LINE_ON_TIME_COLORS: Record<string, string> = {
  on_time: "bg-emerald-50 text-emerald-700 border-emerald-200",
  at_risk: "bg-amber-50 text-amber-700 border-amber-200",
  late: "bg-red-50 text-red-700 border-red-200",
  unknown: "bg-muted text-muted-foreground border-border",
};

// ───────────────────────── Customer-facing CoA (outbound) ──────────────────
// Document state for customer-facing finished-product Certificates of Analysis.
// Distinct from the inbound (raw-material) COA_STATUS above which encodes
// pass/fail of test results.
export const CUSTOMER_COA_STATUS_LABELS: Record<string, string> = {
  issued: "Issued",
  revoked: "Revoked",
  superseded: "Superseded",
};

export const CUSTOMER_COA_STATUS_COLORS: Record<string, string> = {
  issued: "bg-green-100 text-green-800 border-green-200",
  revoked: "bg-red-100 text-red-800 border-red-200",
  superseded: "bg-gray-100 text-gray-800 border-gray-200",
};

// ─────────────────── Manufacturer's Declaration (item-level) ───────────────
// Customs/import declaration issued per stock item. Same lifecycle shape as the
// customer-facing CoA above; kept separate so the two can diverge later.
// Spec: docs/2026-06-17-manufacturer-declaration-and-customs-coa-design.md
export const MD_STATUS_LABELS: Record<string, string> = {
  issued: "Issued",
  revoked: "Revoked",
  superseded: "Superseded",
};

export const MD_STATUS_COLORS: Record<string, string> = {
  issued: "bg-green-100 text-green-800 border-green-200",
  revoked: "bg-red-100 text-red-800 border-red-200",
  superseded: "bg-gray-100 text-gray-800 border-gray-200",
};

/**
 * Certificate of Composition status. Same three terminal-forward states as the
 * Manufacturer's Declaration — issued documents are never edited, only revoked
 * (with a reason) or superseded by a reissue.
 */
export const COC_STATUS_LABELS: Record<string, string> = {
  issued: "Issued",
  revoked: "Revoked",
  superseded: "Superseded",
};

export const COC_STATUS_COLORS: Record<string, string> = {
  issued: "bg-green-100 text-green-800 border-green-200",
  revoked: "bg-red-100 text-red-800 border-red-200",
  superseded: "bg-gray-100 text-gray-800 border-gray-200",
};

export const CUSTOMER_COA_DECLARATION_LABELS: Record<string, string> = {
  gmo_free: "GMO-free",
  gluten_free: "Gluten-free",
  dairy_free: "Dairy-free",
  soy_free: "Soy-free",
  nut_free: "Nut-free",
  shellfish_free: "Shellfish-free",
  egg_free: "Egg-free",
  irradiated_free: "Not irradiated",
  bse_tse_free: "BSE/TSE-free",
  melamine_free: "Melamine-free",
  heavy_metals_spec: "Heavy metals within spec",
  microbiological_spec: "Microbiological within spec",
  wada_compliant: "WADA-compliant (no banned substances)",
};
// ── NPD Sample Dispatch — Status & Customer Email ────────────────────────────
// Source-of-truth maps for npd_sample_dispatches.status and
// npd_sample_dispatches.customer_email_status. Consumed by
// SampleDispatchHistoryTab, SampleDispatchPostCreateView, and
// SampleDispatchEmailStatus. Status enum values come from the DB CHECK
// constraint in 20260805170200_npd_sample_dispatches_table.sql.

export type SampleDispatchStatus =
  | "pending_label"
  | "packing"
  | "labelled"
  | "dispatched"
  | "delivered"
  | "failed"
  | "cancelled";

export const SAMPLE_DISPATCH_STATUS_LABELS: Record<SampleDispatchStatus, string> = {
  pending_label: "Awaiting label",
  packing: "Packing",
  labelled: "Labelled",
  dispatched: "Dispatched",
  delivered: "Delivered",
  failed: "Failed",
  cancelled: "Cancelled",
} as const;

export const SAMPLE_DISPATCH_STATUS_COLORS: Record<SampleDispatchStatus, string> = {
  pending_label: "bg-amber-100 text-amber-900",
  packing: "bg-indigo-100 text-indigo-900",
  labelled: "bg-blue-100 text-blue-900",
  dispatched: "bg-green-100 text-green-900",
  delivered: "bg-emerald-100 text-emerald-900",
  failed: "bg-red-100 text-red-900",
  cancelled: "bg-muted text-muted-foreground",
} as const;

export const SAMPLE_DISPATCH_STATUS_ICONS: Record<SampleDispatchStatus, ElementType> = {
  pending_label: Clock,
  packing: Package,
  labelled: FileCheck2,
  dispatched: Truck,
  delivered: PackageCheck,
  failed: AlertTriangleIcon,
  cancelled: XCircleIcon,
} as const;

export type CustomerEmailStatus = "not_requested" | "pending" | "sent" | "failed";

export const CUSTOMER_EMAIL_STATUS_LABELS: Record<CustomerEmailStatus, string> = {
  not_requested: "No email",
  pending: "Pending",
  sent: "Sent",
  failed: "Failed",
} as const;

export const CUSTOMER_EMAIL_STATUS_COLORS: Record<CustomerEmailStatus, string> = {
  not_requested: "bg-muted text-muted-foreground",
  pending: "bg-amber-100 text-amber-900 hover:bg-amber-100",
  sent: "bg-green-100 text-green-900 hover:bg-green-100",
  failed: "bg-red-100 text-red-900 hover:bg-red-100",
} as const;

// ── Documents ───────────────────────────────────────────────────────────────

export type DocumentStatus = "draft" | "pending_review" | "approved" | "superseded" | "retired";
export type DocumentCategory = "sop" | "manual" | "policy" | "training" | "safety" | "quality" | "hr" | "other";

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: "Draft",
  pending_review: "Pending Review",
  approved: "Approved",
  superseded: "Superseded",
  retired: "Retired",
};

export const DOCUMENT_STATUS_COLORS: Record<DocumentStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  pending_review: "bg-warning/20 text-warning-foreground",
  approved: "bg-success/20 text-success",
  superseded: "bg-muted text-muted-foreground",
  retired: "bg-destructive/20 text-destructive",
};

export const DOCUMENT_STATUS_ICONS: Record<DocumentStatus, ElementType> = {
  draft: FileText,
  pending_review: FileClock,
  approved: FileCheck2,
  superseded: Clock,
  retired: AlertCircle,
};

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  sop: "SOP",
  manual: "Manual",
  policy: "Policy",
  training: "Training",
  safety: "Safety",
  quality: "Quality",
  hr: "HR",
  other: "Other",
};

// `not_requested` intentionally has no icon when rendered — components should
// check for the MinusCircle case and either render it muted or skip the icon.
// Mapping it to MinusCircle keeps the Record exhaustive (TypeScript catches a
// missed status at compile time).
export const CUSTOMER_EMAIL_STATUS_ICONS: Record<CustomerEmailStatus, ElementType> = {
  not_requested: MinusCircle,
  pending: Hourglass,
  sent: CheckCircle2,
  failed: XOctagon,
} as const;

/**
 * Live carrier tracking statuses returned by the shippo-shipping `track` action
 * (Shippo Tracking API). Distinct from SAMPLE_DISPATCH_STATUS_* (internal dispatch
 * lifecycle states) and from the existing TRACKING_STATUS_* maps (TrackShip/purchase-order
 * module, lowercase keys). Used only by the Labels & Tracking tab's live status chip.
 *
 * Named SHIPPO_TRACKING_STATUS_* to avoid collision with the pre-existing
 * TRACKING_STATUS_COLORS / TRACKING_STATUS_LABELS (TrackShip, lowercase-key scheme).
 */
export type ShippoTrackingStatus =
  | "PRE_TRANSIT"
  | "TRANSIT"
  | "DELIVERED"
  | "RETURNED"
  | "FAILURE"
  | "UNKNOWN";

export const SHIPPO_TRACKING_STATUS_LABELS: Record<ShippoTrackingStatus, string> = {
  PRE_TRANSIT: "Label created",
  TRANSIT: "In transit",
  DELIVERED: "Delivered",
  RETURNED: "Returned",
  FAILURE: "Tracking issue",
  UNKNOWN: "Unknown",
};

export const SHIPPO_TRACKING_STATUS_COLORS: Record<ShippoTrackingStatus, string> = {
  PRE_TRANSIT: "bg-slate-100 text-slate-700 border-slate-200",
  TRANSIT: "bg-blue-100 text-blue-700 border-blue-200",
  DELIVERED: "bg-green-100 text-green-700 border-green-200",
  RETURNED: "bg-amber-100 text-amber-700 border-amber-200",
  FAILURE: "bg-red-100 text-red-700 border-red-200",
  UNKNOWN: "bg-slate-100 text-slate-500 border-slate-200",
};

// ── Signing Requests (OpenSign) ─────────────────────────────────────────────
//
// The 10 lifecycle states of a `signing_requests` row driven by the OpenSign
// e-signature integration (Edge Function `opensign-create-request` + provider
// webhooks). Distinct from onboarding-package statuses (draft/sent/in_progress/
// submitted/approved/rejected) which live in the CRM components and are NOT
// owned here. Used by the signing dialog, CRM dashboard and onboarding list.

export type SigningStatus =
  | "draft"
  | "queued"
  | "sent"
  | "viewed"
  | "partially_signed"
  | "completed"
  | "declined"
  | "expired"
  | "revoked"
  | "failed";

export const SIGNING_STATUS_LABELS: Record<SigningStatus, string> = {
  draft: "Draft",
  queued: "Queued",
  sent: "Sent",
  viewed: "Viewed",
  partially_signed: "Partially Signed",
  completed: "Completed",
  declined: "Declined",
  expired: "Expired",
  revoked: "Revoked",
  failed: "Failed",
} as const;

export const SIGNING_STATUS_COLORS: Record<SigningStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  queued: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-700",
  viewed: "bg-info/15 text-info",
  partially_signed: "bg-amber-100 text-amber-700",
  completed: "bg-success/15 text-success",
  declined: "bg-destructive/15 text-destructive",
  expired: "bg-orange-100 text-orange-700",
  revoked: "bg-muted text-muted-foreground",
  failed: "bg-destructive/15 text-destructive",
} as const;

export const SIGNING_STATUS_ICONS: Record<SigningStatus, ElementType> = {
  draft: FileText,
  queued: Hourglass,
  sent: Send,
  viewed: Eye,
  partially_signed: PenLine,
  completed: CheckCircle2,
  declined: XCircleIcon,
  expired: CalendarX,
  revoked: Ban,
  failed: AlertTriangleIcon,
} as const;

/**
 * Signing statuses considered "in flight" — a request that has been dispatched
 * but is not yet in a terminal state (completed/declined/expired/revoked/failed).
 * Centralised so the CRM "Active Signatures" count and any future filters share
 * one definition rather than hardcoding the array inline.
 */
export const SIGNING_ACTIVE_STATUSES: readonly SigningStatus[] = [
  "queued",
  "sent",
  "viewed",
  "partially_signed",
] as const;

// ── Quote status (CRM) ───────────────────────────────────────────────────
// quotes.status: draft → sent → accepted → converted, plus sampling
// (customer proceeded to flavour sampling — quote parked in development).
export const QUOTE_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  sampling: "In sampling",
  accepted: "Accepted",
  converted: "Converted",
  rejected: "Rejected",
  archived: "Archived",
};

export const QUOTE_STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-blue-100 text-blue-700 border-blue-200",
  sampling: "bg-purple-100 text-purple-700 border-purple-200",
  accepted: "bg-teal-100 text-teal-700 border-teal-200",
  converted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  archived: "bg-gray-100 text-gray-600 border-gray-200",
};

// ── Quote line sampling status ───────────────────────────────────────────
export const QUOTE_SAMPLING_STATUS_LABELS: Record<string, string> = {
  required: "Sampling required",
  in_development: "In flavour development",
  completed: "Flavours approved",
};

export const QUOTE_SAMPLING_STATUS_COLORS: Record<string, string> = {
  required: "bg-amber-100 text-amber-700 border-amber-200",
  in_development: "bg-purple-100 text-purple-700 border-purple-200",
  completed: "bg-green-100 text-green-700 border-green-200",
};

// ── NPD Priority List flag categories ───────────────────────────────────────
// Spec 2026-06-22-npd-priority-list-design.md §2 (D5). Mirrors the DB CHECK
// constraint on npd_priority_list.flag_category and the RPC validation.

export const PRIORITY_FLAG_CATEGORIES: PriorityFlagCategory[] = [
  "missing_ingredients",
  "needs_ordering",
  "ordered_not_arrived",
  "needs_customer_clarification",
  "other",
];

export const PRIORITY_FLAG_CATEGORY_LABELS: Record<PriorityFlagCategory, string> = {
  missing_ingredients: "Missing ingredients",
  needs_ordering: "Needs ordering",
  ordered_not_arrived: "Ordered — not arrived",
  needs_customer_clarification: "Needs customer clarification",
  other: "Other",
};

export const PRIORITY_FLAG_CATEGORY_COLORS: Record<PriorityFlagCategory, string> = {
  missing_ingredients: "bg-red-100 text-red-700 border-red-200",
  needs_ordering: "bg-amber-100 text-amber-700 border-amber-200",
  ordered_not_arrived: "bg-blue-100 text-blue-700 border-blue-200",
  needs_customer_clarification: "bg-purple-100 text-purple-700 border-purple-200",
  other: "bg-gray-100 text-gray-700 border-gray-200",
};

// Priority List park state badges (web-only surface; no StatusMaps.swift mirror needed).
export const PRIORITY_PARK_BADGE = {
  followUpDue: { label: "Follow-up due", className: "bg-amber-100 text-amber-700 border-amber-200" },
  parked: { label: "Waiting on customer", className: "bg-slate-100 text-slate-600 border-slate-200" },
} as const;

export const PRIORITY_KEPT_ACTIVE_BADGE = {
  label: "Kept active",
  className: "bg-blue-100 text-blue-700 border-blue-200",
} as const;

// ── Document Instances (CRM onboarding / NDA) ────────────────────────────────
//
// Lifecycle states for `document_instances` rows — tracks the end-to-end
// progress of a sent document (e.g. Mutual NDA) through the signing pipeline.
// Mirrors the `status` CHECK constraint in the document_instances migration.
// Used by useDocumentInstances hook + the staff NDA-send UI components.

export type DocumentInstanceStatus =
  | "draft"
  | "ready_for_signature"
  | "sent"
  | "partially_signed"
  | "completed"
  | "declined"
  | "voided"
  | "expired";

export const DOCUMENT_INSTANCE_STATUS_LABELS: Record<DocumentInstanceStatus, string> = {
  draft: "Draft",
  ready_for_signature: "Ready for Signature",
  sent: "Sent",
  partially_signed: "Partially Signed",
  completed: "Completed",
  declined: "Declined",
  voided: "Voided",
  expired: "Expired",
} as const;

export const DOCUMENT_INSTANCE_STATUS_COLORS: Record<DocumentInstanceStatus, string> = {
  // Neutral holding states — not yet in flight
  draft: "bg-muted text-muted-foreground",
  ready_for_signature: "bg-slate-100 text-slate-700",
  // Active/in-flight states
  sent: "bg-blue-100 text-blue-700",
  partially_signed: "bg-amber-100 text-amber-700",
  // Terminal: positive
  completed: "bg-success/15 text-success",
  // Terminal: negative
  declined: "bg-destructive/15 text-destructive",
  voided: "bg-muted text-muted-foreground",
  expired: "bg-orange-100 text-orange-700",
} as const;

// ── RKB Validator Verdicts (Increment 4) ──────────────────────────────────────
// Verdict taxonomy written by the Inc 3 validator into label_proof_scan_checks.verdict.
// Consumed by ScanFindingsPanel to render a coloured verdict chip on each finding row.
// The `satisfies Record<Verdict, …>` guard makes a missing/renamed verdict a
// COMPILE error (lint:strict) instead of a silent `?? fallback` at runtime.
export type Verdict =
  | "verified"
  | "potential_discrepancy"
  | "evidence_missing"
  | "unable_to_verify"
  | "not_assessable"
  | "human_review"
  // Inert: verbatim wording whose legislative source record isn't verified yet
  // (governed-layers rewire, sub-plan 4). Never pass/fail — awaiting back-sourcing.
  | "awaiting_verified_source";

export const VERDICT_LABELS = {
  verified: "Verified",
  potential_discrepancy: "Potential discrepancy",
  evidence_missing: "Evidence missing",
  unable_to_verify: "Unable to verify",
  not_assessable: "Not assessable",
  human_review: "Human review",
  awaiting_verified_source: "Awaiting verified source",
} satisfies Record<Verdict, string>;

export const VERDICT_COLORS = {
  verified: "bg-success/20 text-success",
  potential_discrepancy: "bg-destructive/20 text-destructive",
  evidence_missing: "bg-warning/20 text-warning-foreground",
  unable_to_verify: "bg-warning/20 text-warning-foreground",
  not_assessable: "bg-muted text-muted-foreground",
  // Distinct from warning — indicates a person must review, not just an AI gap.
  human_review: "bg-primary/20 text-primary",
  // Amber: not a pass — the wording could not be confirmed (source unverified). Drags the
  // readiness score (excluded from `passed` in recalculateScore) but does not hard-block print.
  awaiting_verified_source: "bg-warning/20 text-warning-foreground",
} satisfies Record<Verdict, string>;

export const VERDICT_ICONS = {
  verified: CheckCircle2,
  potential_discrepancy: AlertTriangleIcon,
  evidence_missing: AlertTriangleIcon,
  unable_to_verify: HelpCircle,
  not_assessable: MinusCircle,
  human_review: UserCheck,
  awaiting_verified_source: Clock,
} satisfies Record<Verdict, ElementType>;
// ci: re-trigger PR Applicability Gate on #790 (rebuilt-on-main head did not fire the gate)

// ── WS-A finding provenance display maps (obligation enrichment) ──────────────
// Human-readable labels for the evidence-source / regulatory-basis / risk values stamped on
// each rkb_validation finding's provenance. Display only — no logic depends on these.
export const EVIDENCE_SOURCE_LABELS: Record<string, string> = {
  artwork: "Artwork",
  fps: "Finished Product Spec",
  ingredient_spec: "Ingredient Specification",
  certificate: "Certificate",
  lab_report: "Laboratory Report",
  coo_documentation: "Country-of-Origin Docs",
  gs1_registry: "GS1 Registry",
  print_spec: "Print Specification",
};
export const REGULATORY_BASIS_LABELS: Record<string, string> = {
  consumer_safety: "Consumer safety",
  legal_requirement: "Legal requirement",
  consumer_information: "Consumer information",
  traceability: "Traceability",
  production: "Production",
  recall_prevention: "Recall prevention",
};
export const RISK_CATEGORY_LABELS: Record<string, string> = {
  critical: "Critical",
  major: "Major",
  minor: "Minor",
  information: "Information",
};

// ── Regulatory Source Library (RKB Source UI) ─────────────────────────────────
// verification_status on regulatory_source_records. Badge variant idiom mirrors
// IngredientLibrary's ternary-to-variant pattern: verified -> default,
// pending_verification -> secondary, draft/superseded -> outline.
export const REGULATORY_SOURCE_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_verification: "Pending verification",
  verified: "Verified",
  superseded: "Superseded",
} as const;

export const REGULATORY_SOURCE_STATUS_VARIANTS: Record<string, "default" | "secondary" | "outline"> = {
  draft: "outline",
  pending_verification: "secondary",
  verified: "default",
  superseded: "outline",
} as const;

// ── NPD Ingredient Nutrition Provenance (CoA Nutrition Capture, Task 6) ───────
// Trust tier stamped onto npd_ingredients.nutrition_source: by apply_coa_nutrition
// (Task 2 RPC) when a CoA is verified, or "manual" when a user hand-edits a
// nutrition value in IngredientForm. Lowest to highest trust:
// manual < provisional < unverified_coa < verified_coa. NULL/undefined = untouched.
// The `satisfies Record<NutritionSource, …>` guard makes a missing/renamed tier a
// compile error (lint:strict) instead of a silent runtime fallback.
export type NutritionSource = "verified_coa" | "unverified_coa" | "provisional" | "manual";

export const NUTRITION_SOURCE_LABELS = {
  verified_coa: "Verified CoA",
  unverified_coa: "Unverified CoA",
  provisional: "Provisional",
  manual: "Manual",
} satisfies Record<NutritionSource, string>;

// shadcn Badge `variant` values: "default" | "secondary" | "destructive" | "outline"
export const NUTRITION_SOURCE_VARIANTS = {
  verified_coa: "default",
  unverified_coa: "secondary",
  provisional: "outline",
  manual: "outline",
} satisfies Record<NutritionSource, "default" | "secondary" | "destructive" | "outline">;

// ── Supplier Qualification (P1e) ─────────────────────────────────────────────
// Spine state machine, material hazard tier, supplier assurance level, and
// performance band. Colours use the shared token classes (bg-*/text-*) so they
// stay theme-consistent with the rest of the map. Never inline these in a
// component (CLAUDE.md §5).

// qualification_state on supplier_material_qualifications.
export type QualificationState =
  | "not_qualified"
  | "provisional"
  | "qualified"
  | "conditional_emergency"
  | "suspended";

export const QUALIFICATION_STATE_LABELS = {
  not_qualified: "Not Qualified",
  provisional: "Provisional",
  qualified: "Qualified",
  conditional_emergency: "Conditional (Emergency)",
  suspended: "Suspended",
} satisfies Record<QualificationState, string>;

export const QUALIFICATION_STATE_COLORS = {
  not_qualified: "bg-muted text-muted-foreground",
  provisional: "bg-warning/20 text-warning-foreground",
  qualified: "bg-success/20 text-success",
  conditional_emergency: "bg-orange-100 text-orange-700",
  suspended: "bg-destructive/20 text-destructive",
} satisfies Record<QualificationState, string>;

// material_tier / supplier_assurance — L / M / H (green / amber / red).
export type RiskTier = "L" | "M" | "H";

export const MATERIAL_TIER_LABELS = {
  L: "Tier L (Low)",
  M: "Tier M (Medium)",
  H: "Tier H (High)",
} satisfies Record<RiskTier, string>;

export const MATERIAL_TIER_COLORS = {
  L: "bg-success/20 text-success",
  M: "bg-warning/20 text-warning-foreground",
  H: "bg-destructive/20 text-destructive",
} satisfies Record<RiskTier, string>;

export const SUPPLIER_ASSURANCE_LABELS = {
  L: "Assurance L (Low)",
  M: "Assurance M (Medium)",
  H: "Assurance H (High)",
} satisfies Record<RiskTier, string>;

export const SUPPLIER_ASSURANCE_COLORS = {
  L: "bg-destructive/20 text-destructive",
  M: "bg-warning/20 text-warning-foreground",
  H: "bg-success/20 text-success",
} satisfies Record<RiskTier, string>;

// performance_band on supplier_material_qualifications.
export type PerformanceBand = "excellent" | "acceptable" | "watch" | "unproven";

export const PERFORMANCE_BAND_LABELS = {
  excellent: "Excellent",
  acceptable: "Acceptable",
  watch: "Watch",
  unproven: "Unproven",
} satisfies Record<PerformanceBand, string>;

export const PERFORMANCE_BAND_COLORS = {
  excellent: "bg-success/20 text-success",
  acceptable: "bg-primary/20 text-primary",
  watch: "bg-warning/20 text-warning-foreground",
  unproven: "bg-muted text-muted-foreground",
} satisfies Record<PerformanceBand, string>;

// Autonomy band — the doc-gate/verification engine's recommendation for a
// supplier×material (P2a sqm_recommend_band): red (mandatory human review),
// amber (assisted one-click confirm/reject), green (auto-clear — hard-off).
export type AutonomyBand = "red" | "amber" | "green";

export const AUTONOMY_BAND_LABELS = {
  red: "Mandatory review",
  amber: "Assisted",
  green: "Auto-clear",
} satisfies Record<AutonomyBand, string>;

export const AUTONOMY_BAND_COLORS = {
  red: "bg-destructive/20 text-destructive",
  amber: "bg-warning/20 text-warning-foreground",
  green: "bg-success/20 text-success",
} satisfies Record<AutonomyBand, string>;

// One-line human explanation of the reason code sqm_recommend_band returns.
export const AUTONOMY_BAND_REASON_LABELS: Record<string, string> = {
  tier_1_high_mandatory_review: "Tier 1 High material — mandatory review",
  conformance_watch: "Conformance on watch",
  overseas_or_fraud_uncertified: "Overseas / fraud-risk from an uncertified supplier",
  mandatory_review: "Mandatory review",
  auto_cleared: "Auto-cleared",
  green_band_disabled: "Would auto-clear — held for review (auto-clear disabled)",
  documents_incomplete: "Required documents incomplete",
  not_yet_evaluated: "Not yet evaluated",
  manual_qualification_required: "Manual qualification decision required",
  material_not_classified: "Material not yet classified",
};

// ── Supplier Qualification grain (P-A / P-B — QF-AU-SUP-ADD-003) ──────────────
// qualification_scope.decision, legacy_migration_state, the L/H/P/O/G source
// classification, and the sqm_scope_evaluate approvable/blocked verdict. Never
// inline these in a component (CLAUDE.md §5).

// qualification_scope.decision
export type ScopeDecision =
  | "draft"
  | "approved"
  | "conditional"
  | "rejected"
  | "suspended"
  | "dequalified";

export const SCOPE_DECISION_LABELS = {
  draft: "Draft",
  approved: "Approved",
  conditional: "Conditional",
  rejected: "Rejected",
  suspended: "Suspended",
  dequalified: "Dequalified",
} satisfies Record<ScopeDecision, string>;

export const SCOPE_DECISION_COLORS = {
  draft: "bg-muted text-muted-foreground",
  approved: "bg-success/20 text-success",
  conditional: "bg-orange-100 text-orange-700",
  rejected: "bg-destructive/20 text-destructive",
  suspended: "bg-destructive/20 text-destructive",
  dequalified: "bg-destructive/20 text-destructive",
} satisfies Record<ScopeDecision, string>;

// qualification_scope.legacy_migration_state
export type ScopeMigrationState =
  | "LEGACY_SCOPE_PENDING"
  | "MIGRATED"
  | "EXCEPTION_ACTIVE"
  | "RETIRED";

export const SCOPE_MIGRATION_STATE_LABELS = {
  LEGACY_SCOPE_PENDING: "Legacy (pending scope)",
  MIGRATED: "Migrated",
  EXCEPTION_ACTIVE: "Exception (active)",
  RETIRED: "Retired",
} satisfies Record<ScopeMigrationState, string>;

export const SCOPE_MIGRATION_STATE_COLORS = {
  LEGACY_SCOPE_PENDING: "bg-warning/20 text-warning-foreground",
  MIGRATED: "bg-success/20 text-success",
  EXCEPTION_ACTIVE: "bg-orange-100 text-orange-700",
  RETIRED: "bg-muted text-muted-foreground",
} satisfies Record<ScopeMigrationState, string>;

// L/H/P/O/G source classification on a decision reason (D4).
export type SourceClass = "L" | "H" | "P" | "O" | "G";

export const SOURCE_CLASS_LABELS = {
  L: "Australian legal / FSANZ",
  H: "Codex HACCP / GHP",
  P: "ISO PRP",
  O: "Superior Formulations control",
  G: "Guidance",
} satisfies Record<SourceClass, string>;

export const SOURCE_CLASS_COLORS = {
  L: "bg-destructive/20 text-destructive",
  H: "bg-primary/20 text-primary",
  P: "bg-blue-100 text-blue-700",
  O: "bg-warning/20 text-warning-foreground",
  G: "bg-muted text-muted-foreground",
} satisfies Record<SourceClass, string>;

// sqm_scope_evaluate verdict.
export type ScopeVerdict = "approvable" | "blocked";

export const SCOPE_VERDICT_LABELS = {
  approvable: "Approvable",
  blocked: "Blocked",
} satisfies Record<ScopeVerdict, string>;

export const SCOPE_VERDICT_COLORS = {
  approvable: "bg-success/20 text-success",
  blocked: "bg-destructive/20 text-destructive",
} satisfies Record<ScopeVerdict, string>;

// supplier_critical_events severity (§10.3). P0 Critical … P3 Informational.
export type CriticalEventSeverity = "P0" | "P1" | "P2" | "P3";

export const CRITICAL_EVENT_SEVERITY_LABELS = {
  P0: "P0 — Critical",
  P1: "P1 — Significant",
  P2: "P2 — Administrative",
  P3: "P3 — Informational",
} satisfies Record<CriticalEventSeverity, string>;

export const CRITICAL_EVENT_SEVERITY_COLORS = {
  P0: "bg-destructive/20 text-destructive",
  P1: "bg-orange-100 text-orange-700",
  P2: "bg-warning/20 text-warning-foreground",
  P3: "bg-muted text-muted-foreground",
} satisfies Record<CriticalEventSeverity, string>;

// supplier_critical_events.status
export type CriticalEventStatus = "open" | "contained" | "investigating" | "closed";

export const CRITICAL_EVENT_STATUS_LABELS = {
  open: "Open",
  contained: "Contained",
  investigating: "Investigating",
  closed: "Closed",
} satisfies Record<CriticalEventStatus, string>;

export const CRITICAL_EVENT_STATUS_COLORS = {
  open: "bg-destructive/20 text-destructive",
  contained: "bg-warning/20 text-warning-foreground",
  investigating: "bg-primary/20 text-primary",
  closed: "bg-success/20 text-success",
} satisfies Record<CriticalEventStatus, string>;

// supplier_trace_requests.status (§10.4 — 4-hour trace pack).
export type TraceRequestStatus = "open" | "partial" | "complete" | "incomplete_closed";

export const TRACE_REQUEST_STATUS_LABELS = {
  open: "Open",
  partial: "Partial",
  complete: "Complete",
  incomplete_closed: "Closed — incomplete",
} satisfies Record<TraceRequestStatus, string>;

export const TRACE_REQUEST_STATUS_COLORS = {
  open: "bg-destructive/20 text-destructive",
  partial: "bg-warning/20 text-warning-foreground",
  complete: "bg-success/20 text-success",
  incomplete_closed: "bg-orange-100 text-orange-700",
} satisfies Record<TraceRequestStatus, string>;

// supplier_change_notices.status (§10.1).
export type ChangeNoticeStatus = "submitted" | "impact_review" | "on_hold" | "released" | "rejected";

export const CHANGE_NOTICE_STATUS_LABELS = {
  submitted: "Submitted",
  impact_review: "Impact review",
  on_hold: "On hold",
  released: "Released",
  rejected: "Rejected",
} satisfies Record<ChangeNoticeStatus, string>;

export const CHANGE_NOTICE_STATUS_COLORS = {
  submitted: "bg-primary/20 text-primary",
  impact_review: "bg-warning/20 text-warning-foreground",
  on_hold: "bg-destructive/20 text-destructive",
  released: "bg-success/20 text-success",
  rejected: "bg-muted text-muted-foreground",
} satisfies Record<ChangeNoticeStatus, string>;

// supplier_nonconformances severity + status (§10.5) — distinct from the general
// quality NCR maps above (different status vocabulary), so prefixed SUPPLIER_NCR_.
export type SupplierNcrSeverity = "critical" | "major" | "minor";

export const SUPPLIER_NCR_SEVERITY_COLORS = {
  critical: "bg-destructive/20 text-destructive",
  major: "bg-orange-100 text-orange-700",
  minor: "bg-warning/20 text-warning-foreground",
} satisfies Record<SupplierNcrSeverity, string>;

export type SupplierNcrStatus = "open" | "contained" | "rca" | "capa" | "effectiveness_review" | "closed";

export const SUPPLIER_NCR_STATUS_LABELS = {
  open: "Open",
  contained: "Contained",
  rca: "Root-cause",
  capa: "CAPA",
  effectiveness_review: "Effectiveness review",
  closed: "Closed",
} satisfies Record<SupplierNcrStatus, string>;

export const SUPPLIER_NCR_STATUS_COLORS = {
  open: "bg-destructive/20 text-destructive",
  contained: "bg-warning/20 text-warning-foreground",
  rca: "bg-primary/20 text-primary",
  capa: "bg-primary/20 text-primary",
  effectiveness_review: "bg-orange-100 text-orange-700",
  closed: "bg-success/20 text-success",
} satisfies Record<SupplierNcrStatus, string>;

// ── MRPeasy Mirror Lot Status ──────────────────────────────────────────────

export const MIRROR_LOT_STATUS_LABELS: Record<string, string> = {
  on_order: "On Order",
  on_hold: "On Hold (QC)",
  available: "Available",
  expired: "Expired",
  // Also emitted by mapLotStatus (60/80) and the parity view (synthesized):
  inspection: "Inspection",
  rejected: "Rejected",
  "pending mirror": "Pending Mirror",
} as const;

export const MIRROR_LOT_STATUS_COLORS: Record<string, string> = {
  on_order: "bg-info/15 text-info",
  on_hold: "bg-warning/15 text-warning",
  available: "bg-success/15 text-success",
  expired: "bg-destructive/15 text-destructive",
  inspection: "bg-warning/15 text-warning",
  rejected: "bg-destructive/15 text-destructive",
  "pending mirror": "bg-muted text-muted-foreground",
} as const;

// HACCP impact outcome (§10.2).
export const HACCP_OUTCOME_LABELS: Record<string, string> = {
  no_impact: "No impact",
  document_update: "Document update only",
  hazard_revision: "Hazard analysis revision",
  ccp_reassessment: "CCP reassessment",
  validation_required: "Validation / trial required",
  label_change: "Label / artwork change",
  supply_not_approved: "Supply not approved",
};

// Human labels for the mandatory trace-pack field keys (§10.4).
export const TRACE_PACK_FIELD_LABELS: Record<string, string> = {
  material_supplier_lot: "Material + supplier lot",
  qf_material_po_delivery: "QF material / PO / delivery IDs",
  manufacturer_site: "Manufacturer + physical site",
  manufacture_pack_dispatch_dates: "Manufacture / pack / dispatch dates",
  quantities: "Quantities (made / supplied / held / distributed)",
  mass_balance: "Mass-balance reconciliation",
  upstream_sub_supplier_lots: "Upstream / sub-supplier lots",
  coa_coc_release_records: "CoA / CoC / release records",
  transport_storage: "Transport + storage",
  affected_lot_range: "Affected lot range + rationale",
  containment_status: "Containment status + contact",
};

// ── SRKB LIL (Legislative Intelligence) — Change Feed badges (Phase 6) ─────────
// Risk (regulatory_change_impact_assessments.risk), obligation/proposal lifecycle
// (srkb_change_proposals.lifecycle_state / proposal_status). Unknown → neutral fallback.
export interface LilBadgeMeta {
  label: string;
  className: string;
}

const LIL_BADGE_FALLBACK: LilBadgeMeta = {
  label: "Unknown",
  className: "bg-slate-100 text-slate-600 border-slate-200",
};

export const RCIA_RISK_MAP: Record<string, LilBadgeMeta> = {
  high: { label: "High risk", className: "bg-red-100 text-red-700 border-red-200" },
  medium: { label: "Medium risk", className: "bg-amber-100 text-amber-700 border-amber-200" },
  low: { label: "Low risk", className: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const LIL_LIFECYCLE_MAP: Record<string, LilBadgeMeta> = {
  extracted: { label: "Extracted", className: "bg-slate-100 text-slate-600 border-slate-200" },
  normalised: { label: "Normalised", className: "bg-blue-100 text-blue-700 border-blue-200" },
  legislatively_verified: { label: "Legislatively verified", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  superseded: { label: "Superseded", className: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

export const PROPOSAL_STATUS_MAP: Record<string, LilBadgeMeta> = {
  queued: { label: "Queued", className: "bg-amber-100 text-amber-700 border-amber-200" },
  in_review: { label: "In review", className: "bg-blue-100 text-blue-700 border-blue-200" },
  authored: { label: "Authored", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  dismissed: { label: "Dismissed", className: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

export function getRiskMeta(risk: string | null | undefined): LilBadgeMeta {
  return (risk && RCIA_RISK_MAP[risk]) || LIL_BADGE_FALLBACK;
}
export function getLifecycleMeta(state: string | null | undefined): LilBadgeMeta {
  return (state && LIL_LIFECYCLE_MAP[state]) || LIL_BADGE_FALLBACK;
}
export function getProposalStatusMeta(status: string | null | undefined): LilBadgeMeta {
  return (status && PROPOSAL_STATUS_MAP[status]) || LIL_BADGE_FALLBACK;
}

// ── Material document extraction status (slice 4a, material-doc-upload-surface) ────
// supplier_item_documents.extraction_status — badge shown on the material document
// register (StockItemDocumentsSection). AI-extracted data behind this status is
// ADVISORY ONLY / unverified (NOTES-MES.md AI-extraction-surface rule); the badge
// must never be styled to look like a pass/verification result.

export const EXTRACTION_STATUS_COLORS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  extracting: "bg-info/15 text-info",
  extracted: "bg-success/15 text-success",
  failed: "bg-destructive/15 text-destructive",
  not_extractable: "bg-muted text-muted-foreground",
} as const;

export const EXTRACTION_STATUS_LABELS: Record<string, string> = {
  pending: "Pending extraction",
  extracting: "Extracting…",
  extracted: "AI extracted",
  failed: "Extraction failed",
  not_extractable: "Not AI-scanned",
} as const;

export const EXTRACTION_STATUS_ICONS: Record<string, ElementType> = {
  pending: Clock,
  extracting: FileClock,
  extracted: CheckCircle2,
  failed: XCircleIcon,
  not_extractable: Ban,
} as const;

// ---------------------------------------------------------------------------
// Material fact ledger — status + provenance (slice 4c)
// ---------------------------------------------------------------------------
// The wording carries weight here. A 'proposed' fact is an AI extraction nobody
// has checked, and NOTES-NPD records what happens when such data is presented as
// though it were a finding: "Contains: None" reached a customer certificate off
// the back of an unassessed column. Labels therefore say what the fact IS, never
// what it claims — "Awaiting verification", not "Detected".
export const MATERIAL_FACT_STATUS_COLORS: Record<string, string> = {
  proposed: "bg-warning/15 text-warning",
  verified: "bg-success/15 text-success",
  rejected: "bg-destructive/15 text-destructive",
  superseded: "bg-muted text-muted-foreground",
} as const;

export const MATERIAL_FACT_STATUS_LABELS: Record<string, string> = {
  proposed: "Awaiting verification",
  verified: "Verified",
  rejected: "Rejected",
  superseded: "Superseded",
} as const;

export const MATERIAL_FACT_STATUS_ICONS: Record<string, ElementType> = {
  proposed: Clock,
  verified: CheckCircle2,
  rejected: XCircleIcon,
  superseded: Ban,
} as const;

// Provenance answers "where did this come from", which is the question a reviewer
// actually needs before attesting. legacy_typed is called out explicitly because
// it is the one with NO source document — it cannot be verified in one click and
// the UI must not offer that.
export const MATERIAL_FACT_PROVENANCE_LABELS: Record<string, string> = {
  document_ai: "AI-read from document",
  inbox_ai: "AI-read from emailed document",
  manual: "Entered by hand",
  // Migration 20280602000000. Says BOTH halves on purpose: a reviewer deciding
  // whether to sign this off needs to know a person typed it (so the figure is
  // only as good as their reading) AND that a document backs it (so there is
  // something to check it against). "Entered by hand" alone reads as unevidenced;
  // "AI-read from document" — what this row displayed before manual_cited existed
  // — is worse, because it credits a model that never saw the page.
  manual_cited: "Entered by hand from a cited document",
  legacy_typed: "Pre-existing typed value — no source document",
} as const;

// ── Contractor shells (Contractor Onboarding Phase A1) ───────────────────────
// Lifecycle of a prospective contractor record, from first contact through to an
// onboarded contractor. Mirrors the CHECK on contractor_shells.status
// (20280401000000): prospect | engaged | onboarded | archived. Kept here rather
// than inline in ContractorShellList so the next surface that renders a shell
// status — the Phase C "send agreement" view — cannot drift a second copy.
export type ContractorShellStatus = "prospect" | "engaged" | "onboarded" | "archived";

export const CONTRACTOR_SHELL_STATUS_LABELS: Record<ContractorShellStatus, string> = {
  prospect: "Prospect",
  engaged: "Engaged",
  onboarded: "Onboarded",
  archived: "Archived",
} as const;

// Badge variants, not colour classes: this list renders shadcn <Badge variant=…>,
// and the repo's other variant maps (REGULATORY_SOURCE_STATUS_VARIANTS,
// NUTRITION_SOURCE_VARIANTS) use the same shape.
export const CONTRACTOR_SHELL_STATUS_VARIANTS: Record<
  ContractorShellStatus,
  "default" | "secondary" | "outline"
> = {
  prospect: "secondary",
  engaged: "default",
  onboarded: "outline",
  archived: "outline",
} as const;

// ── Batch release (batch_release_records.status + "not started") ─────────────
// The release state of a finished batch as the MO detail shows it on BOTH platforms
// (web BatchReleasePanel / worklist; iOS MODetailView via mo_batch_release_status).
// `not_started` is not a table value — it is what the RPC returns when no record exists.
// Mirrored in ios/Operations App/Utilities/StatusMaps.swift (CLAUDE.md §14 rule 3); the
// parity guard is src/lib/__tests__/batchReleaseStatusParity.test.ts.
export type BatchReleaseDisplayStatus = "not_started" | "draft" | "released" | "rejected";

export const BATCH_RELEASE_STATUS_LABELS: Record<BatchReleaseDisplayStatus, string> = {
  not_started: "Release not started",
  draft: "Awaiting release",
  released: "Released",
  rejected: "Rejected",
} as const;

export const BATCH_RELEASE_STATUS_VARIANTS: Record<
  BatchReleaseDisplayStatus,
  "default" | "secondary" | "outline" | "destructive"
> = {
  not_started: "outline",
  draft: "default",
  released: "secondary",
  rejected: "destructive",
} as const;

// ── Digital batch record (batch_records.status) ───────────────────────────────
// The eBR per MO (20260314160000 B11). BatchRecordTab imported these two maps since it was
// written and they never existed — two baselined TS2305 errors on an unrouted tab. Vocabulary
// is the table's CHECK: open | in_progress | pending_review | approved | rejected | voided.
export const BATCH_RECORD_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  pending_review: "Pending Review",
  approved: "Approved",
  rejected: "Rejected",
  voided: "Voided",
} as const;

export const BATCH_RECORD_STATUS_COLORS: Record<string, string> = {
  open: "bg-muted text-muted-foreground",
  in_progress: "bg-amber-100 text-amber-700",
  pending_review: "bg-blue-100 text-blue-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  voided: "bg-slate-100 text-slate-700",
} as const;

// ── MRPeasy push (mrpeasy_push_jobs.status / mrpeasy_push_steps.status) ────
// Slice 1 (items) + slice 2 (routings) push
// (docs/superpowers/specs/2026-09-18-mrpeasy-push-design.md). Step vocabulary: pending
// (queued, not yet attempted) | adopted (matched an existing MRPeasy item, no create
// needed) | created (new item written) | verified (readback matched the request) |
// mismatch (readback disagreed — needs a human) | blocked (a routing step's {$ref}
// dependency — its item step — did not resolve to a usable MRPeasy id, so the routing
// step itself was never attempted; resolveDependency in the mrpeasy-push worker is the
// sole author) | failed (the API call itself errored). NOTE the tenant-busy case is NOT
// a step state: a lease refusal parks the whole JOB in `waiting_tenant` (below) and no
// step is touched.
// `adopted`/`created`/`verified` are all TERMINAL SATISFIED states — none of them may carry
// `mismatch`/`failed`'s destructive styling, the same rule this file already states for
// MO_ITEM_STATUS_COLORS.consumed: a satisfied outcome rendering red is the defect the
// vocabulary exists to prevent.
export const MRPEASY_PUSH_STEP_STATUS_COLORS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  adopted: "bg-emerald-100 text-emerald-700",
  created: "bg-emerald-100 text-emerald-700",
  verified: "bg-green-100 text-green-700",
  mismatch: "bg-red-100 text-red-700",
  blocked: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  unknown: "bg-muted text-muted-foreground",
} as const;

export const MRPEASY_PUSH_STEP_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  adopted: "Adopted",
  created: "Created",
  verified: "Verified",
  mismatch: "Mismatch",
  blocked: "Blocked — dependency not usable",
  failed: "Failed",
  unknown: "Unknown",
} as const;

/**
 * Resolve a raw `mrpeasy_push_steps.status` to a key both maps above hold.
 * Same `hasOwnProperty` guard as `moItemStatusKey` — plain `in` walks the prototype
 * chain and a status of "toString"/"constructor" would resolve to a function, which
 * React would then render as the badge label and interpolate raw into className.
 */
export function mrpeasyPushStepStatusKey(status: string | null | undefined): string {
  return status && Object.prototype.hasOwnProperty.call(MRPEASY_PUSH_STEP_STATUS_LABELS, status)
    ? status
    : "unknown";
}

// Job vocabulary: queued | running | waiting_tenant (the mrpeasy-sync lease is held by
// another cycle — this is a normal, self-resolving wait, not a failure) | partial (some
// steps landed, some did not — resumable) | complete | failed | cancelled.
export const MRPEASY_PUSH_JOB_STATUS_LABELS: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  waiting_tenant: "Waiting for MRPeasy access",
  partial: "Partially complete",
  complete: "Complete",
  failed: "Failed",
  cancelled: "Cancelled",
  unknown: "Unknown",
} as const;

export function mrpeasyPushJobStatusKey(status: string | null | undefined): string {
  return status && Object.prototype.hasOwnProperty.call(MRPEASY_PUSH_JOB_STATUS_LABELS, status)
    ? status
    : "unknown";
}

/**
 * The job is still moving — worth polling, and reason enough to keep the "Push items"
 * button disabled (mrpeasy_push_jobs_one_active_per_formulation is a real DB constraint;
 * enqueueing a second one while these are live would just surface as a 23505 toast).
 * Single source for this set: the hook's polling gate and the panel's disable gate must
 * agree, or the button can look enabled while a poll is still running underneath it.
 */
export const MRPEASY_PUSH_ACTIVE_JOB_STATUSES = new Set([
  "queued",
  "running",
  "waiting_tenant",
  "partial",
]);

export function isMrpeasyPushJobActive(status: string | null | undefined): boolean {
  return !!status && MRPEASY_PUSH_ACTIVE_JOB_STATUSES.has(status);
}

// ── Label scan pass status (plan P-5a) ───────────────────────────────────────
// One pass of a label scan: reading the label, or one check. "not_in_scan" is
// a legacy run that predates a check (its status column is NULL) — never shown
// as a failure. Mirrored in StatusMaps.swift (scanPassStatus*).
export type ScanPassStatus = "waiting" | "running" | "passed" | "issues" | "failed" | "not_in_scan";

export const SCAN_PASS_STATUS_LABELS: Record<ScanPassStatus, string> = {
  waiting: "Waiting",
  running: "Checking…",
  passed: "Passed",
  issues: "Issues",
  failed: "Did not run",
  not_in_scan: "Not part of this scan",
};

export const SCAN_PASS_STATUS_ICONS: Record<ScanPassStatus, ElementType> = {
  waiting: Circle,
  running: Loader2,
  passed: CheckCircle2,
  issues: AlertTriangleIcon,
  failed: XCircleIcon,
  not_in_scan: MinusCircle,
};

export const SCAN_PASS_STATUS_COLORS: Record<ScanPassStatus, string> = {
  waiting: "text-muted-foreground",
  running: "text-teal-600 animate-spin",
  passed: "text-emerald-600",
  issues: "text-amber-600",
  failed: "text-destructive",
  not_in_scan: "text-muted-foreground",
};

// Gate 1: the FPS compliance sign-off before an FPS is sent (plan P-4).
// Mirrored in StatusMaps.swift (fpsCompliance*).
export type FpsComplianceState = "not_checked" | "checking" | "failed" | "blocked" | "ready_to_sign" | "signed";

export const FPS_COMPLIANCE_STATE_LABELS: Record<FpsComplianceState, string> = {
  not_checked: "Not checked",
  checking: "Checking…",
  failed: "Check failed",
  blocked: "Issues to resolve",
  ready_to_sign: "Ready to sign",
  signed: "Signed",
};

export const FPS_COMPLIANCE_STATE_BADGE_VARIANTS: Record<FpsComplianceState, "default" | "destructive" | "outline"> = {
  not_checked: "outline",
  checking: "outline",
  failed: "destructive",
  blocked: "destructive",
  ready_to_sign: "outline",
  signed: "default",
};

// ── Label requirement outcomes (one requirement, one flag, 2026-10-18) ──────
// One outcome per requirement per scan (v_label_requirement_status).
// Mirrored in ios/Operations App/Utilities/StatusMaps.swift.
export type LabelRequirementOutcome = "issue" | "to_confirm" | "passed" | "confirmed" | "not_applicable";

export const LABEL_REQUIREMENT_OUTCOME_LABELS: Record<LabelRequirementOutcome, string> = {
  issue: "Issue",
  to_confirm: "To confirm",
  passed: "Passed",
  confirmed: "Confirmed",
  not_applicable: "Not applicable",
};

export const LABEL_REQUIREMENT_OUTCOME_COLORS: Record<LabelRequirementOutcome, string> = {
  issue: "bg-red-50 text-red-700 border-red-200",
  to_confirm: "bg-amber-50 text-amber-800 border-amber-200",
  passed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_applicable: "bg-gray-50 text-gray-600 border-gray-200",
};

// ── Allergen changeover (P-011 §4.7) ────────────────────────────────────────
// Mirrored in ios/Operations App/Utilities/StatusMaps.swift.

export type AllergenChangeoverStatus = "open" | "evidenced" | "superseded" | "historical";

export const ALLERGEN_CHANGEOVER_STATUS_LABELS: Record<AllergenChangeoverStatus, string> = {
  open: "Clean-down needed",
  evidenced: "Clean-down verified",
  superseded: "Re-planned",
  historical: "Before go-live",
};

export const ALLERGEN_CHANGEOVER_STATUS_BADGE_VARIANTS: Record<AllergenChangeoverStatus, "destructive" | "outline" | "secondary"> = {
  open: "destructive",
  evidenced: "outline",
  superseded: "secondary",
  historical: "secondary",
};

/** Why a Gate 1 finding is not a real problem. No "accepted risk": a real issue is fixed, not waived. */
export const FPS_COMPLIANCE_DISPOSITION_REASONS: Array<{ value: "false_positive" | "not_applicable" | "other"; label: string }> = [
  { value: "false_positive", label: "The FPS does meet it (check was wrong)" },
  { value: "not_applicable", label: "Does not apply to this product" },
  { value: "other", label: "Other" },
];

// Compliance dashboard gate tone (plan P-7b). Mirrored in StatusMaps.swift
// (complianceGateTone*).
export type ComplianceGateTone = "pass" | "warn" | "fail" | "none";

export const COMPLIANCE_GATE_TONE_CLASSES: Record<ComplianceGateTone, string> = {
  pass: "bg-emerald-100 text-emerald-800 border-emerald-200",
  warn: "bg-amber-100 text-amber-800 border-amber-200",
  fail: "bg-red-100 text-red-800 border-red-200",
  none: "bg-muted text-muted-foreground border-border",
};

// Compliance audit timeline event labels (plan P-7c). Unknown event types fall
// back to a humanised form of the raw type (complianceTimelineEventLabel).
export const COMPLIANCE_TIMELINE_EVENT_LABELS: Record<string, string> = {
  fps_check_started: "Compliance check started",
  fps_check_completed: "Compliance check completed",
  fps_check_failed: "Compliance check failed",
  fps_finding_disposed: "Finding disposed",
  fps_signed: "Compliance signed off",
  scan_run: "Label scan run",
  scan_pass_rerun: "Scan pass re-run",
  scan_pass_completed_manually: "Scan pass completed manually",
  regulatory_approved: "Regulatory approval",
  released: "Label released",
  version_uploaded: "Artwork version uploaded",
  project_created: "Proof project created",
  reviewer_link_sent: "Reviewer link sent",
  invitation_sent: "Invitation sent",
  session_opened: "Reviewer opened the canvas",
  annotation_added: "Annotation added",
  checklist_item_updated: "Checklist item updated",
  checklist_ai_resolved: "Checklist item pre-filled by AI",
  ai_scan_complete: "AI scan complete",
  token_refreshed: "Reviewer link refreshed",
  category_changed: "Product category changed",
};

export function complianceTimelineEventLabel(event: string): string {
  const known = COMPLIANCE_TIMELINE_EVENT_LABELS[event];
  if (known) return known;
  const words = event.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Event";
}

/** SRKB verdict -> the four user-facing words (plan vocabulary). Anything else is "Needs human check". */
export const FPS_COMPLIANCE_VERDICT_LABELS: Record<string, string> = {
  verified: "Pass",
  potential_discrepancy: "Issue",
  not_applicable: "Not applicable",
};
